import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { MagazineCard } from '../components/MagazineCard';
import {
  Download, BookOpen, ArrowLeft, ChevronRight, ChevronLeft,
  Calendar, Layers, Tag, FileText, ExternalLink, X, Clock,
  Stethoscope, GraduationCap, Building2, Landmark, Cpu, Shield, TrendingUp, Users, Briefcase,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Volume/Issue formatter ───────────────────────────────────────────────────
// Outputs: VOL. 6 NO. 07  (no middle-dot between VOL and NO)
const fmtVolIssue = (vol, num) => {
  const v = vol || 6;
  const n = String(num).padStart(2, '0');
  return `VOL. ${v} NO. ${n}`;
};
// Full metadata: JULY 2026 · VOL. 6 NO. 07
const fmtFullMeta = (issueDate, vol, num) => {
  const datePart = issueDate ? issueDate.toUpperCase() : '';
  const volPart = fmtVolIssue(vol, num);
  return datePart ? `${datePart} · ${volPart}` : volPart;
};

// ─── Loading Spinner ─────────────────────────────────────────────────────────
const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--op-paper)' }}>
    <div style={{ textAlign: 'center' }}>
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-4"
        style={{ borderColor: 'var(--op-teal)' }} />
      <p style={{ color: 'var(--op-muted)', fontSize: '0.875rem' }}>Loading magazine…</p>
    </div>
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    published: { label: 'Published', bg: '#D1FAE5', color: '#065F46' },
    draft:     { label: 'Draft',     bg: '#F3F4F6', color: '#6B7280' },
    review:    { label: 'In Review', bg: '#DBEAFE', color: '#1D4ED8' },
    scheduled: { label: 'Scheduled', bg: '#FEF3C7', color: '#92400E' },
    archived:  { label: 'Archived',  bg: '#FEE2E2', color: '#991B1B' },
  };
  const s = map[status] || map.draft;
  return (
    <span
      className="text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
      style={{ background: s.bg, color: s.color }}
      data-testid="issue-status-badge"
    >
      {s.label}
    </span>
  );
};

// ─── Archive Grid Card ────────────────────────────────────────────────────────
const ArchiveCard = ({ issue }) => (
  <div
    className="group rounded-2xl overflow-hidden shadow-sm border hover:shadow-lg transition-all duration-300"
    style={{ borderColor: 'var(--op-border)', background: 'var(--op-paper-2)' }}
    data-testid="archive-card"
  >
    {/* Cover */}
    <div className="relative overflow-hidden" style={{ height: 220 }}>
      {issue.cover_image_url ? (
        <img src={issue.cover_image_url} alt={issue.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          data-testid="archive-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-3"
          style={{ background: 'linear-gradient(160deg,#081D30 0%,#102F43 55%,#006E6A 100%)' }}>
          <div className="w-14 h-14 rounded-xl flex items-center justify-center font-black text-xl"
            style={{ background: 'rgba(255,253,248,.12)', border: '2px solid rgba(255,253,248,.2)', color: '#FFFDF8' }}>
            OP
          </div>
          <p className="text-white text-sm font-semibold" style={{ letterSpacing: '-0.02em' }}>{issue.issue_date}</p>
        </div>
      )}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to top,rgba(8,29,48,.5) 0%,transparent 50%)' }} />
      {issue.is_latest && (
        <span className="absolute top-3 left-3 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
          style={{ background: 'var(--op-teal)', color: 'white' }}>
          Current Issue
        </span>
      )}
    </div>

    {/* Content */}
    <div className="p-5">
      <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--op-teal)' }}>
        {issue.issue_date}
      </p>
      <h3 className="font-bold text-base leading-tight mb-1" style={{ color: 'var(--op-ink)', letterSpacing: '-0.02em' }}
        data-testid="archive-title">
        {issue.title}
      </h3>
      {issue.subtitle && (
        <p className="text-sm mb-3" style={{ color: 'var(--op-muted)' }}>{issue.subtitle}</p>
      )}

      <div className="flex gap-2 mt-4">
        <Link
          to={`/magazine/${issue.slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-sm font-bold transition-all duration-200 hover:-translate-y-0.5"
          style={{ background: 'var(--op-navy)', color: 'white' }}
          data-testid="archive-read-btn"
        >
          <BookOpen size={14} /> Read
        </Link>
        {issue.download_pdf_url && (
          <a
            href={issue.download_pdf_url}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-sm font-bold transition-all duration-200 hover:-translate-y-0.5"
            style={{ border: '1.5px solid var(--op-border)', color: 'var(--op-ink)', background: 'transparent' }}
            data-testid="archive-download-btn"
          >
            <Download size={14} /> PDF
          </a>
        )}
      </div>
    </div>
  </div>
);

// ─── PUBLIC MAGAZINE LANDING PAGE (prototype fidelity pass) ─────────────────
export const MagazinePage = () => {
  const [issues, setIssues]         = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    // Fetch all issues so the Previous Issue card can use staged monthly covers (including drafts)
    axios.get(`${API}/magazine?issue_status=published`)
      .then(r => setIssues(r.data))
      .catch(e => console.error('Failed to load magazine issues:', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  // Hero: use the published is_latest issue; fall back to first published
  const latestIssue = issues.find(i => i.is_latest && i.issue_status === 'published')
    || issues.find(i => i.issue_status === 'published')
    || null;
  const tocItems = latestIssue?.toc_entries?.length > 0
    ? latestIssue.toc_entries.slice(0, 5).map(e => ({ title: e.title, label: e.section_label || '' }))
    : (latestIssue?.table_of_contents || []).slice(0, 5).map((t, i) => ({ title: t, label: ['Education','Business','Policy','Innovation','People'][i] || '' }));
  const hasRealDigital = latestIssue?.fliphtml5_embed_url && !latestIssue.fliphtml5_embed_url.includes('example.com');

  // Card issue: prefer published is_latest with a real cover, otherwise pick most-recent cover-bearing non-hidden issue
  const HIDE_FROM_CARD = ['jan-feb-2026','opportunity-post-monthly-jan-feb-2026-sample'];
  const cardIssue =
    (latestIssue && latestIssue.cover_image_url && !HIDE_FROM_CARD.includes(latestIssue.slug) ? latestIssue : null)
    || issues
         .filter(i => i.cover_image_url && !HIDE_FROM_CARD.includes(i.slug))
         .sort((a, b) => (b.issue_number || 0) - (a.issue_number || 0))[0]
    || null;
  const cardHasDigital = cardIssue?.fliphtml5_embed_url && !cardIssue.fliphtml5_embed_url.includes('example.com');

  return (
    <div className="magazine-proto" data-testid="magazine-page">
      <style>{`
        .magazine-proto {
          --navy:var(--mp-navy);--teal:var(--mp-teal);--teal-h:var(--mp-teal-h);--teal-d:var(--mp-teal-d);
          --gold:var(--mp-gold);--gold-s:var(--mp-gold-s);--paper:var(--mp-paper);--paper2:var(--mp-paper2);
          --paper3:var(--mp-paper3);--bg:var(--mp-bg);--surface:var(--mp-surface);--border:var(--mp-border);
          --div:var(--mp-div);--text:var(--mp-text);--muted:var(--mp-muted);--faint:var(--mp-faint);
          --sp:.25rem;--sp2:.5rem;--sp3:.75rem;--sp4:1rem;--sp5:1.25rem;--sp6:1.5rem;--sp8:2rem;--sp10:2.5rem;--sp12:3rem;--sp16:4rem;--sp20:5rem;--sp24:6rem;
          --r-sm:.5rem;--r-md:.85rem;--r-lg:1.25rem;--r-xl:1.85rem;--r-2xl:2.4rem;--r-full:999px;
          --sh-sm:0 4px 14px rgba(8,25,38,.07);--sh-md:0 14px 44px rgba(8,25,38,.11);--sh-lg:0 30px 80px rgba(8,25,38,.17);--sh-xl:0 48px 120px rgba(8,25,38,.24);
          --ease-out:cubic-bezier(.16,1,.3,1);--t:180ms var(--ease-out);
          --text-xs:clamp(.72rem,.68rem + .2vw,.82rem);--text-sm:clamp(.875rem,.82rem + .28vw,1rem);
          --text-base:clamp(1rem,.96rem + .2vw,1.1rem);--text-hero:clamp(2.4rem,1.2rem + 2.6vw,4.2rem);
          --font-display:'Instrument Serif',Georgia,serif;--font-body:'Inter',system-ui,sans-serif;--w-wide:1220px;
          background:var(--paper2);font-family:var(--font-body);font-size:var(--text-base);color:var(--text);line-height:1.65;min-height:100vh;
        }
        .magazine-proto h1,.magazine-proto h2,.magazine-proto h3,.magazine-proto h4{text-wrap:balance;line-height:1.08}
        .magazine-proto p,.magazine-proto li{text-wrap:pretty;max-width:72ch}
        .magazine-proto ul[role="list"]{list-style:none;padding:0;margin:0}
        .magazine-proto a{color:inherit;text-decoration:none}
        .magazine-proto button,.magazine-proto input{font:inherit;cursor:pointer}
        .magazine-proto .wrap{width:min(calc(100% - var(--sp8)),var(--w-wide));margin:0 auto}
        .magazine-proto .fade-in{opacity:1}
        @supports(animation-timeline:scroll()){
          .magazine-proto .fade-in{opacity:0;animation:mp-fade-reveal linear both;animation-timeline:view();animation-range:entry 0% entry 72%}
          @keyframes mp-fade-reveal{to{opacity:1}}
        }
        .magazine-proto .btn{display:inline-flex;align-items:center;justify-content:center;gap:.45rem;min-height:48px;padding:.85rem 1.35rem;border-radius:var(--r-lg);border:1px solid transparent;font-size:var(--text-sm);font-weight:800;white-space:nowrap;transition:background var(--t),color var(--t),box-shadow var(--t),transform var(--t),border-color var(--t);text-decoration:none}
        .magazine-proto .btn:hover{transform:translateY(-1.5px)}
        .magazine-proto .btn:active{transform:translateY(0)}
        .magazine-proto .btn-primary{background:linear-gradient(135deg,var(--teal),var(--teal-d));color:#fff;box-shadow:0 6px 18px rgba(14,107,101,.28)}
        .magazine-proto .btn-primary:hover{background:linear-gradient(135deg,var(--teal-h),var(--teal-d));box-shadow:0 10px 28px rgba(14,107,101,.36);color:#fff}
        .magazine-proto .btn-dark{background:var(--navy);color:#fff;box-shadow:var(--sh-sm)}
        .magazine-proto .btn-dark:hover{background:#0e2a3c;box-shadow:var(--sh-md);color:#fff}
        .magazine-proto .btn-ghost{background:rgba(255,255,255,.72);border-color:var(--border);color:var(--text);box-shadow:var(--sh-sm)}
        .magazine-proto .btn-ghost:hover{background:#fff;box-shadow:var(--sh-md)}
        .magazine-proto .btn-gold{background:linear-gradient(135deg,var(--gold),#a07820);color:#fff;box-shadow:0 6px 18px rgba(196,150,48,.3)}
        .magazine-proto .btn-gold:hover{box-shadow:0 10px 28px rgba(196,150,48,.42);color:#fff}
        .magazine-proto .sc{background:rgba(255,255,255,.68);border:1px solid var(--border);border-radius:var(--r-xl);box-shadow:var(--sh-sm)}
        .magazine-proto .hero{padding:clamp(1.6rem,3vw,2.8rem) 0 0}
        .magazine-proto .hero-grid{display:grid;grid-template-columns:minmax(0,1.08fr) minmax(280px,.92fr);gap:clamp(1.5rem,3.5vw,3.5rem);align-items:flex-end;padding-bottom:clamp(1.6rem,3vw,2.8rem)}
        .magazine-proto .eyebrow{display:inline-flex;align-items:center;gap:.5rem;padding:.42rem .85rem;border-radius:var(--r-full);background:rgba(14,107,101,.08);border:1px solid rgba(14,107,101,.13);font-size:var(--text-xs);font-weight:900;letter-spacing:.06em;text-transform:uppercase;color:var(--teal-d);margin-bottom:var(--sp4)}
        .magazine-proto .hero h1{font-family:'Newsreader',Georgia,serif;font-size:var(--text-hero);font-weight:500;letter-spacing:-.018em;line-height:.95;color:var(--navy)}
        .magazine-proto .hero-sub{margin-top:var(--sp2);font-size:clamp(.95rem,.9rem + .12vw,1.05rem);color:#324555;font-weight:600;max-width:34ch;line-height:1.42}
        .magazine-proto .hero-tag{margin-top:var(--sp2);font-size:.9rem;font-weight:800;color:#a87820;font-style:italic}
        .magazine-proto .hero-tagline{margin-top:var(--sp2);font-size:.98rem;font-weight:800;color:var(--navy);font-style:italic;letter-spacing:-.01em;line-height:1.4;max-width:34ch}
        .magazine-proto .hero-body{margin-top:var(--sp3);color:var(--muted);max-width:52ch;font-size:var(--text-sm);line-height:1.7}
        .magazine-proto .hero-cta{margin-top:var(--sp5);display:flex;flex-wrap:wrap;gap:.9rem}
        .magazine-proto .hero-social{margin-top:var(--sp4);font-size:var(--text-xs);font-weight:700;color:var(--muted)}
        .magazine-proto .hero-social strong{color:var(--navy)}
        .magazine-proto .hero-advert{margin-top:var(--sp5);display:inline-flex;align-items:center;gap:.45rem;color:var(--teal);font-size:var(--text-sm);font-weight:800}
        .magazine-proto .hero-advert:hover{color:var(--teal-h)}
        .magazine-proto .hero-visual{position:relative;min-height:460px;display:flex;align-items:center;justify-content:center}
        .magazine-proto .orb-bg{position:absolute;inset:0;border-radius:50%;background:radial-gradient(circle at 52% 44%,rgba(232,204,138,.95) 0%,rgba(201,150,48,.75) 4%,rgba(201,150,48,.35) 10%,rgba(14,61,68,.18) 22%,rgba(14,61,68,0) 40%),radial-gradient(circle at 50% 50%,rgba(14,107,101,.22) 0%,rgba(14,107,101,.08) 40%,transparent 68%);filter:blur(1px);animation:mp-orb-breathe 8s ease-in-out infinite}
        @keyframes mp-orb-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
        .magazine-proto .orb-ring{position:absolute;border-radius:50%;border:1px solid rgba(201,150,48,.12);animation:mp-ring-pulse 6s ease-in-out infinite}
        .magazine-proto .orb-ring:nth-child(2){inset:8%;animation-delay:0s}
        .magazine-proto .orb-ring:nth-child(3){inset:18%;animation-delay:1.5s}
        .magazine-proto .orb-ring:nth-child(4){inset:28%;animation-delay:3s}
        @keyframes mp-ring-pulse{0%,100%{opacity:.55}50%{opacity:1}}
        .magazine-proto .issue-card{position:relative;z-index:2;width:min(100%,380px);background:linear-gradient(175deg,#fffef9,#faf6ee);border:1px solid rgba(9,28,40,.07);border-radius:var(--r-xl);padding:1.35rem;box-shadow:var(--sh-xl);transition:transform var(--t),box-shadow var(--t)}
        .magazine-proto .issue-card:hover{transform:translateY(-4px) rotate(.25deg);box-shadow:0 56px 130px rgba(8,25,38,.24)}
        .magazine-proto .ic-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:.95rem}
        .magazine-proto .ic-pill{display:inline-flex;align-items:center;gap:.35rem;padding:.32rem .65rem;background:var(--navy);color:#fff;border-radius:var(--r-full);font-size:.68rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase}
        .magazine-proto .ic-date{font-size:var(--text-xs);font-weight:700;color:var(--faint)}
        .magazine-proto .ic-title{font-family:var(--font-display);font-size:clamp(2rem,1.8rem + .5vw,2.6rem);line-height:.94;color:var(--navy);margin-bottom:.3rem}
        .magazine-proto .ic-brand{color:var(--muted);font-size:var(--text-xs);font-weight:700;margin-bottom:.85rem;display:flex;align-items:center;gap:.45rem}
        .magazine-proto .ic-cover{width:100%;height:260px;border-radius:8px;overflow:hidden;position:relative;background:linear-gradient(135deg,#0a2230 0%,#0f3c4e 42%,#0b2432 100%);margin-bottom:1rem;flex-shrink:0}
        .magazine-proto .ic-cover img{width:100%;height:100%;object-fit:cover;display:block;transition:transform 0.4s ease}
        .magazine-proto .issue-card:hover .ic-cover img{transform:scale(1.03)}
        .magazine-proto .ic-cover-fallback{position:absolute;inset:0;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.92) 0%,rgba(235,195,94,.45) 7%,transparent 15%),radial-gradient(circle at 14% 108%,rgba(18,90,98,.98) 0%,rgba(10,52,68,.98) 22%,rgba(7,36,50,1) 40%),radial-gradient(circle at 50% 94%,rgba(15,95,100,.85) 0%,rgba(15,95,100,.85) 24%,rgba(7,36,50,0) 28%)}
        .magazine-proto .ic-cover-label{position:absolute;bottom:10px;left:10px;font-family:'IBM Plex Mono','Courier New',monospace;font-size:9px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#fff;background:rgba(7,36,50,.65);padding:4px 8px;border-radius:4px;backdrop-filter:blur(4px)}
        .magazine-proto .ic-issue-title{font-family:'Newsreader',Georgia,serif;font-size:clamp(1rem,.95rem + .2vw,1.15rem);font-weight:500;color:var(--navy);line-height:1.35;margin-bottom:.4rem;letter-spacing:-.01em}
        .magazine-proto .ic-meta-row{font-size:var(--text-xs);font-weight:800;color:var(--teal);letter-spacing:.06em;text-transform:uppercase;margin-bottom:.25rem}
        .magazine-proto .ic-stats{font-size:var(--text-xs);color:var(--muted);font-weight:600;margin-bottom:.85rem}
        .magazine-proto .ic-ctas{display:flex;gap:.6rem;flex-wrap:wrap}
        .magazine-proto .ic-read-btn{display:inline-flex;align-items:center;padding:.5rem .95rem;border-radius:8px;background:var(--navy);color:#fff;font-size:var(--text-sm);font-weight:700;text-decoration:none;transition:background .18s ease;letter-spacing:-.01em;white-space:nowrap}
        .magazine-proto .ic-read-btn:hover{background:#0A283C;color:#fff}
        .magazine-proto .ic-digital-btn{display:inline-flex;align-items:center;padding:.5rem .95rem;border-radius:8px;border:1.5px solid var(--div);color:var(--ink);font-size:var(--text-sm);font-weight:700;text-decoration:none;background:transparent;transition:border-color .18s;letter-spacing:-.01em;white-space:nowrap}
        .magazine-proto .ic-digital-btn:hover{border-color:var(--navy)}
        .magazine-proto .ic-foot{display:none}
        .magazine-proto .trust-bar{margin:0 0 var(--sp6);border-top:1px solid var(--div);border-bottom:1px solid var(--div);background:rgba(255,255,255,.42)}
        .magazine-proto .trust-inner{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;padding:1.05rem 0}
        .magazine-proto .trust-item{display:flex;align-items:center;gap:.65rem;padding:0 var(--sp6)}
        .magazine-proto .trust-item:not(:last-child){border-right:1px solid var(--div)}
        .magazine-proto .trust-label{font-size:.875rem;font-weight:700;color:var(--text);line-height:1.4;max-width:185px}
        .magazine-proto .trust-icon{width:2rem;height:2rem;border-radius:6px;background:rgba(9,28,40,.06);border:1px solid rgba(9,28,40,.09);display:flex;align-items:center;justify-content:center;flex-shrink:0}
        .magazine-proto .trust-icon svg{width:.95rem;height:.95rem;stroke:var(--navy);fill:none;stroke-width:1.75;stroke-linecap:round;stroke-linejoin:round}
        .magazine-proto .dig-widget{margin:0 0 var(--sp6);background:linear-gradient(135deg,var(--navy) 0%,#0e2a3c 55%,var(--teal-d) 100%);border-radius:var(--r-xl);overflow:hidden;position:relative}
        .magazine-proto .dig-widget::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 85% 20%,rgba(232,204,138,.18) 0%,transparent 40%),radial-gradient(circle at 10% 80%,rgba(14,107,101,.2) 0%,transparent 35%);pointer-events:none}
        .magazine-proto .dig-inner{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(260px,.6fr);gap:2rem;align-items:center;padding:clamp(2rem,4vw,3rem) clamp(1.5rem,4vw,3rem);position:relative;z-index:1}
        .magazine-proto .dig-kicker{display:inline-flex;align-items:center;gap:.45rem;font-size:var(--text-xs);font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:var(--gold-s);margin-bottom:var(--sp4)}
        .magazine-proto .dig-h2{font-family:var(--font-display);font-size:clamp(2rem,1.6rem + 1.2vw,3rem);font-weight:400;line-height:1.05;color:#fff;margin-bottom:var(--sp4)}
        .magazine-proto .dig-h2 em{color:var(--gold-s);font-style:italic}
        .magazine-proto .dig-desc{color:rgba(255,255,255,.72);font-size:var(--text-sm);line-height:1.75;max-width:52ch;margin-bottom:var(--sp8)}
        .magazine-proto .dig-features{display:flex;flex-wrap:wrap;gap:var(--sp6);margin-bottom:var(--sp8)}
        .magazine-proto .dig-feat{display:flex;align-items:center;gap:.5rem;font-size:var(--text-xs);font-weight:700;color:rgba(255,255,255,.8)}
        .magazine-proto .dig-feat-dot{width:.45rem;height:.45rem;border-radius:50%;background:var(--gold-s);flex-shrink:0}
        .magazine-proto .dig-ctas{display:flex;flex-wrap:wrap;gap:.85rem}
        .magazine-proto .dig-visual{display:flex;align-items:center;justify-content:center;position:relative;min-height:260px}
        .magazine-proto .dig-book{position:relative;z-index:2;width:180px;background:linear-gradient(175deg,#fffef9,#faf6ee);border-radius:3px 8px 8px 3px;box-shadow:16px 18px 40px rgba(0,0,0,.45),3px 0 0 #0e2030;overflow:hidden;transform:perspective(900px) rotateY(-8deg);transition:transform var(--t)}
        .magazine-proto .dig-book:hover{transform:perspective(900px) rotateY(-3deg) translateY(-4px)}
        .magazine-proto .dig-book-art{height:240px;position:relative;background:#0a2230}
        .magazine-proto .dig-book-art-inner{position:absolute;inset:0;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.92) 0%,rgba(235,195,94,.45) 7%,transparent 15%),radial-gradient(circle at 14% 108%,rgba(18,90,98,.98) 0%,rgba(10,52,68,.98) 22%,rgba(7,36,50,1) 40%),radial-gradient(circle at 50% 94%,rgba(15,95,100,.85) 0%,rgba(15,95,100,.85) 24%,rgba(7,36,50,0) 28%)}
        .magazine-proto .dig-book-badge{position:absolute;top:.6rem;right:.6rem;background:var(--gold);color:#fff;font-size:.55rem;font-weight:900;letter-spacing:.08em;text-transform:uppercase;padding:.25rem .5rem;border-radius:var(--r-full);z-index:3}
        .magazine-proto .dig-book-spine{position:absolute;left:0;top:0;bottom:0;width:8px;background:#0e2030;z-index:3}
        .magazine-proto .dig-book-foot{padding:.7rem .8rem;background:#fff}
        .magazine-proto .dig-book-title{font-family:var(--font-display);font-size:1rem;line-height:1;color:var(--navy)}
        .magazine-proto .dig-book-sub{font-size:.62rem;font-weight:700;color:var(--muted);margin-top:.2rem;letter-spacing:.04em;text-transform:uppercase}
        .magazine-proto .dig-glow{position:absolute;inset:-30px;background:radial-gradient(circle at 50% 50%,rgba(14,107,101,.22) 0%,transparent 65%);pointer-events:none}
        .magazine-proto .dig-badge-wrap{position:absolute;bottom:-10px;right:-16px;z-index:3;background:var(--teal);color:#fff;border:3px solid var(--paper2);border-radius:var(--r-full);padding:.55rem 1rem;font-size:var(--text-xs);font-weight:900;box-shadow:var(--sh-md);white-space:nowrap}
        .magazine-proto .latest{padding:var(--sp6) 0}
        .magazine-proto .latest-grid{display:grid;grid-template-columns:240px minmax(0,1fr) 310px;gap:1.5rem;padding:1.6rem}
        .magazine-proto .mini-cover{background:#fff;border:1px solid var(--border);border-radius:var(--r-lg);padding:.9rem;box-shadow:var(--sh-md);width:100%}
        .magazine-proto .mc-date{color:var(--faint);font-size:.7rem;font-weight:800;margin-bottom:.35rem}
        .magazine-proto .mc-title{font-family:var(--font-display);font-size:2rem;line-height:.94;color:var(--navy)}
        .magazine-proto .mc-brand{color:var(--muted);font-size:.7rem;font-weight:700;margin-bottom:.95rem;display:flex;align-items:center;gap:.4rem}
        .magazine-proto .mc-art{height:255px;border-radius:.15rem;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.92) 0%,rgba(235,195,94,.45) 7%,transparent 15%),radial-gradient(circle at 14% 108%,rgba(18,90,98,.98) 0%,rgba(10,52,68,.98) 22%,rgba(7,36,50,1) 40%,rgba(7,36,50,1) 100%),radial-gradient(circle at 50% 94%,rgba(15,95,100,.85) 0%,rgba(15,95,100,.85) 24%,rgba(7,36,50,0) 28%)}
        .magazine-proto .sec-heading{font-family:var(--font-display);font-size:clamp(2rem,1.7rem + .6vw,2.75rem);line-height:1;color:var(--navy)}
        .magazine-proto .meta-row{display:flex;flex-wrap:wrap;gap:1rem;margin-top:.75rem;color:var(--muted);font-size:var(--text-xs);font-weight:800}
        .magazine-proto .meta-row span{display:flex;align-items:center;gap:.38rem}
        .magazine-proto .story-head{margin-top:.95rem;font-weight:900;font-size:1.62rem;line-height:1.02;letter-spacing:-.03em;color:var(--teal);max-width:22ch}
        .magazine-proto .latest-copy p{margin-top:.85rem;color:var(--muted);font-size:var(--text-sm);max-width:60ch}
        .magazine-proto .inline-btns{margin-top:1.35rem;display:flex;flex-wrap:wrap;gap:.85rem}
        .magazine-proto .toc-panel{padding-left:1.4rem;border-left:1px solid var(--div)}
        .magazine-proto .toc-panel h3{font-size:1.12rem;font-weight:900;color:var(--navy);margin-bottom:.95rem}
        .magazine-proto .toc-row{display:grid;grid-template-columns:1.3rem 1fr;gap:.75rem;padding:.8rem 0;border-bottom:1px solid var(--div)}
        .magazine-proto .toc-row:last-child{border-bottom:none;padding-bottom:0}
        .magazine-proto .toc-dot{width:1.2rem;height:1.2rem;border-radius:50%;border:1.5px solid rgba(14,107,101,.5);margin-top:.15rem;position:relative;flex-shrink:0}
        .magazine-proto .toc-dot::after{content:'';position:absolute;inset:.28rem;border-radius:50%;background:rgba(14,107,101,.2)}
        .magazine-proto .toc-row strong{display:block;font-size:var(--text-sm);font-weight:800;color:var(--text);line-height:1.2}
        .magazine-proto .toc-row span{color:var(--muted);font-size:var(--text-xs)}
        .magazine-proto .mid-grid{display:grid;grid-template-columns:minmax(0,1.5fr) 340px;gap:1.5rem;padding:0 0 var(--sp6)}
        .magazine-proto .feat-pad{padding:1.6rem}
        .magazine-proto .feat-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1.1rem;margin-top:1.15rem}
        .magazine-proto .feat-card{padding:1.15rem;border-radius:var(--r-lg);background:rgba(255,255,255,.78);border:1px solid rgba(9,28,40,.07);transition:box-shadow var(--t),transform var(--t)}
        .magazine-proto .feat-card:hover{box-shadow:var(--sh-md);transform:translateY(-2px)}
        .magazine-proto .feat-icon{width:2.5rem;height:2.5rem;border-radius:50%;margin-bottom:.9rem;background:linear-gradient(160deg,rgba(14,107,101,.14),rgba(14,107,101,.06));display:flex;align-items:center;justify-content:center}
        .magazine-proto .feat-icon svg{width:1.1rem;height:1.1rem;stroke:var(--teal);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
        .magazine-proto .feat-card h3{font-size:.94rem;font-weight:900;color:var(--navy);margin-bottom:.48rem;line-height:1.2}
        .magazine-proto .feat-card p{color:var(--muted);font-size:var(--text-xs);line-height:1.6}
        .magazine-proto .archive-pad{padding:1.4rem}
        .magazine-proto .archive-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem}
        .magazine-proto .archive-head a{color:var(--teal);font-size:var(--text-xs);font-weight:800}
        .magazine-proto .arc-row{display:grid;grid-template-columns:1fr .7fr auto auto auto;gap:.55rem;align-items:center;padding:.58rem 0;border-bottom:1px solid var(--div);font-size:var(--text-xs)}
        .magazine-proto .arc-row:last-child{border-bottom:none}
        .magazine-proto .arc-row strong{font-weight:800;color:var(--text)}
        .magazine-proto .arc-row .iss{color:var(--muted);font-weight:700}
        .magazine-proto .arc-row a{color:var(--teal);font-weight:800;transition:color var(--t)}
        .magazine-proto .arc-row a:hover{color:var(--teal-h)}
        .magazine-proto .testi-section{padding:0 0 var(--sp6)}
        .magazine-proto .testi-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1.2rem;margin-top:1.15rem}
        .magazine-proto .testi-card{padding:1.35rem 1.5rem;border-radius:var(--r-xl);background:var(--navy);box-shadow:var(--sh-md);position:relative;overflow:hidden}
        .magazine-proto .testi-card::before{content:'\\201C';position:absolute;top:-.3rem;left:.9rem;font-family:var(--font-display);font-size:6rem;line-height:1;color:rgba(255,255,255,.07);pointer-events:none}
        .magazine-proto .testi-quote{font-family:var(--font-display);font-style:italic;font-size:1.12rem;line-height:1.48;color:rgba(255,255,255,.92);position:relative;z-index:1}
        .magazine-proto .testi-attr{margin-top:1rem;color:rgba(255,255,255,.46);font-size:var(--text-xs);font-weight:700}
        .magazine-proto .testi-attr strong{display:block;color:rgba(255,255,255,.72);margin-bottom:.15rem}
        .magazine-proto .bottom-pair{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:1.5rem;padding:0 0 var(--sp8)}
        .magazine-proto .advertise-card{padding:1.6rem;display:grid;grid-template-columns:1.15fr .85fr;gap:1.25rem;align-items:center}
        .magazine-proto .kicker{font-size:var(--text-xs);font-weight:900;letter-spacing:.08em;text-transform:uppercase;color:var(--gold);margin-bottom:.38rem}
        .magazine-proto .advertise-copy p{color:var(--muted);margin-top:.75rem;font-size:var(--text-sm);max-width:38ch}
        .magazine-proto .stats-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.9rem;margin-top:1.3rem}
        .magazine-proto .stat-item strong{display:block;font-size:1.5rem;font-weight:900;letter-spacing:-.04em;color:var(--navy)}
        .magazine-proto .stat-item span{color:var(--muted);font-size:var(--text-xs);font-weight:700;line-height:1.3}
        .magazine-proto .advert-btns{margin-top:1.4rem;display:flex;flex-wrap:wrap;gap:.8rem}
        .magazine-proto .gold-orb-visual{border-radius:var(--r-xl);min-height:240px;background:radial-gradient(circle at 52% 40%,rgba(212,170,55,.88) 0%,rgba(212,170,55,.5) 10%,rgba(232,204,138,.22) 22%,transparent 34%),radial-gradient(circle at 35% 55%,rgba(240,218,170,.7) 0%,rgba(240,218,170,.38) 18%,transparent 24%),radial-gradient(circle at 68% 62%,rgba(255,255,255,.82) 0%,rgba(255,255,255,.28) 10%,transparent 16%),linear-gradient(165deg,rgba(255,255,255,.22),rgba(255,255,255,.05));position:relative;overflow:hidden}
        .magazine-proto .gold-orb-visual::before,.magazine-proto .gold-orb-visual::after{content:'';position:absolute;border-radius:50%;border:1px solid rgba(201,150,48,.12)}
        .magazine-proto .gold-orb-visual::before{inset:1rem 2rem 3rem 1.5rem}
        .magazine-proto .gold-orb-visual::after{inset:2.5rem .8rem 1.2rem 3.5rem}
        .magazine-proto .signup-card{padding:1.6rem;display:grid;grid-template-columns:1.2fr .8fr;gap:1.25rem;align-items:center}
        .magazine-proto .signup-copy p{color:var(--muted);margin-top:.75rem;font-size:var(--text-sm)}
        .magazine-proto .check-list{margin-top:1rem;display:grid;gap:.55rem;list-style:none;padding:0}
        .magazine-proto .check-list li{display:flex;align-items:center;gap:.6rem;font-size:var(--text-sm);font-weight:700;color:var(--text)}
        .magazine-proto .check-list li::before{content:'';width:.65rem;height:.65rem;border-radius:50%;background:var(--teal);box-shadow:0 0 0 .2rem rgba(14,107,101,.12);flex-shrink:0}
        .magazine-proto .email-form{margin-top:1.4rem;display:flex;gap:.7rem;flex-wrap:wrap}
        .magazine-proto .email-form input{flex:1;min-height:50px;border-radius:var(--r-lg);border:1px solid var(--border);background:#fff;padding:0 1rem;color:var(--text);min-width:160px;transition:border-color var(--t),box-shadow var(--t);font-family:inherit}
        .magazine-proto .email-form input:focus{border-color:var(--teal);box-shadow:0 0 0 3px rgba(14,107,101,.12);outline:none}
        .magazine-proto .privacy{margin-top:.75rem;color:var(--faint);font-size:var(--text-xs)}
        .magazine-proto .mail-visual{min-height:220px;position:relative;display:flex;align-items:center;justify-content:center}
        .magazine-proto .mail-env{position:absolute;width:210px;height:145px;border-radius:var(--r-lg);background:var(--paper3);border:1px solid var(--border);box-shadow:var(--sh-lg);overflow:hidden}
        .magazine-proto .mail-env::before{content:'';position:absolute;top:0;left:0;right:0;height:55%;background:var(--paper);clip-path:polygon(0 0,100% 0,50% 100%)}
        .magazine-proto .mail-env::after{content:'';position:absolute;bottom:0;left:0;right:0;height:55%;background:var(--paper);clip-path:polygon(0 100%,50% 0,100% 100%)}
        .magazine-proto .mail-thumb{position:absolute;left:50%;top:28%;transform:translateX(-50%);width:148px;height:108px;border-radius:.9rem;background:#fff;border:1px solid var(--border);box-shadow:0 10px 28px rgba(8,25,38,.12);overflow:hidden;z-index:2}
        .magazine-proto .mail-thumb-art{position:absolute;inset:0;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.92) 0%,rgba(235,195,94,.45) 7%,transparent 15%),radial-gradient(circle at 14% 108%,rgba(18,90,98,.98) 0%,rgba(10,52,68,.98) 22%,rgba(7,36,50,1) 40%,rgba(7,36,50,1) 100%)}
        .magazine-proto .mail-thumb-label{position:absolute;top:.55rem;left:.6rem;font-family:var(--font-display);font-size:1.25rem;color:#fff;line-height:1;z-index:3}
        .magazine-proto .sub-success{background:rgba(14,107,101,.08);border:1px solid rgba(14,107,101,.2);border-radius:var(--r-lg);padding:1rem 1.25rem;color:var(--teal);font-weight:600;font-size:var(--text-sm);margin-top:1.4rem}
        @media(max-width:1180px){
          .magazine-proto .latest-grid,.magazine-proto .mid-grid,.magazine-proto .bottom-pair,.magazine-proto .advertise-card,.magazine-proto .signup-card{grid-template-columns:1fr}
          .magazine-proto .toc-panel{padding-left:0;border-left:none;border-top:1px solid var(--div);padding-top:1.1rem}
          .magazine-proto .feat-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
          .magazine-proto .testi-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
          .magazine-proto .dig-inner{grid-template-columns:1fr}
          .magazine-proto .dig-visual{min-height:200px}
          .magazine-proto .trust-inner{justify-content:flex-start}
          .magazine-proto .trust-item{border-right:none!important;border-bottom:1px solid var(--div);width:100%}
        }
        @media(max-width:880px){
          .magazine-proto .hero-grid{grid-template-columns:1fr}
          .magazine-proto .hero-visual{min-height:auto;padding-top:var(--sp6)}
          .magazine-proto .orb-bg,.magazine-proto .orb-ring{display:none}
          .magazine-proto .testi-grid{grid-template-columns:1fr}
          .magazine-proto .op-masthead{grid-template-areas:"opp" "post" "desc";grid-template-columns:1fr}
          .magazine-proto .op-masthead-desc{align-self:auto;padding-bottom:0;padding-top:var(--sp4)}
          .magazine-proto .op-masthead-post{white-space:normal}
          .magazine-proto .op-masthead-rule{width:120px}
        }
        @media(max-width:620px){
          .magazine-proto .wrap{width:calc(100% - 1.2rem)}
          .magazine-proto .hero h1{font-size:clamp(3.2rem,12vw,5rem)}
          .magazine-proto .op-masthead-opp,.magazine-proto .op-masthead-post{font-size:clamp(3.2rem,12vw,5rem)}
          .magazine-proto .hero-cta,.magazine-proto .inline-btns,.magazine-proto .advert-btns,.magazine-proto .email-form{flex-direction:column}
          .magazine-proto .btn{width:100%}
          .magazine-proto .feat-grid{grid-template-columns:1fr}
          .magazine-proto .stats-row{grid-template-columns:1fr}
          .magazine-proto .trust-inner{flex-direction:column;align-items:flex-start}
          .magazine-proto .arc-row{grid-template-columns:1fr 1fr;row-gap:.35rem}
          .magazine-proto .arc-row .iss{grid-column:2;grid-row:1}
          .magazine-proto .dig-features{flex-direction:column;gap:.75rem}
        }
        .magazine-proto .op-masthead{display:grid;grid-template-areas:"opp opp" "post desc";grid-template-columns:auto 1fr;column-gap:clamp(18px,2vw,24px)}
        .magazine-proto .op-masthead-opp,.magazine-proto .op-masthead-post{font-family:'Newsreader',Georgia,serif;font-size:var(--text-hero);font-weight:500;letter-spacing:-.018em;line-height:.95;color:var(--navy);text-wrap:balance}
        .magazine-proto .op-masthead-opp{grid-area:opp}
        .magazine-proto .op-masthead-post{grid-area:post;white-space:nowrap}
        .magazine-proto .op-masthead-desc{grid-area:desc;align-self:end;display:flex;flex-direction:column;gap:9px;padding-bottom:0.12em}
        .magazine-proto .op-masthead-rule{width:130px;height:1px;background:#A88745;flex-shrink:0}
        .magazine-proto .op-masthead-label{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;letter-spacing:0.18em;color:#3F6664;text-transform:uppercase;white-space:nowrap}
        .magazine-proto .ci-grid{display:grid;grid-template-columns:minmax(0,1.3fr) 220px minmax(280px,.75fr);align-items:stretch}
        .magazine-proto .ci-left{padding:2.2rem 1.75rem 2.2rem 2rem}
        .magazine-proto .ci-middle{padding:2rem 1.35rem;border-left:1px solid var(--div);border-right:1px solid var(--div)}
        .magazine-proto .ci-middle h3{font-size:1rem;font-weight:900;color:var(--navy);margin-bottom:.95rem}
        .magazine-proto .ci-cover{position:relative;min-height:460px;overflow:hidden}
        .magazine-proto .ci-cover img{width:100%;height:100%;object-fit:cover;display:block;position:absolute;inset:0}
        .magazine-proto .ci-cover-fallback{position:absolute;inset:0;background:linear-gradient(160deg,#081D30 0%,#102F43 55%,#006E6A 100%)}
        @media(max-width:1180px){
          .magazine-proto .ci-grid{grid-template-columns:1fr 1fr}
          .magazine-proto .ci-cover{min-height:300px;grid-column:1/-1;order:0}
          .magazine-proto .ci-left{order:1}
          .magazine-proto .ci-middle{border-left:none;border-right:none;border-top:1px solid var(--div);order:2}
        }
        @media(max-width:620px){
          .magazine-proto .ci-grid{grid-template-columns:1fr}
          .magazine-proto .ci-cover{min-height:240px}
        }
      `}</style>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <div className="eyebrow">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" />
                <circle cx="6" cy="6" r="2" fill="currentColor" />
              </svg>
              Monthly publication &bull; Premium editorial
            </div>
            <div className="op-masthead" data-testid="op-wordmark">
              <span className="op-masthead-opp">Opportunity</span>
              <span className="op-masthead-post">Post</span>
              <div className="op-masthead-desc">
                <div className="op-masthead-rule" />
                <span className="op-masthead-label">M A G A Z I N E</span>
              </div>
            </div>
            <p className="hero-tagline">Where merit is recognized. Fairness is expected. Opportunity finds its audience.</p>
            <p className="hero-tag">Education &bull; Business &bull; Policy &bull; Innovation &bull; People</p>
            <p className="hero-body">A premium digital magazine covering the people, ideas, institutions, and opportunities shaping what comes next. Trusted by leaders, educators, and decision-makers nationwide.</p>
            <div className="hero-cta">
              <a href="#latest" className="btn btn-dark" style={{ borderRadius: '8px' }} data-testid="hero-learn-more-link">Learn About Our Magazine &rarr;</a>
            </div>
            <p className="hero-social">Read by <strong>100K+</strong> monthly readers across education, business &amp; policy.</p>
          </div>
          <div className="hero-visual" aria-hidden="true">
            <div className="orb-bg" />
            <div className="orb-ring" />
            <div className="orb-ring" />
            <div className="orb-ring" />
            <article className="issue-card" data-testid="hero-issue-card">
              {/* Top row: badge + month/year */}
              <div className="ic-top">
                <span className="ic-pill">Latest Issue</span>
                <span className="ic-date">{cardIssue?.issue_date || 'Current Issue'}</span>
              </div>

              {/* Cover image — real cover or fallback gradient */}
              <div className="ic-cover">
                {cardIssue?.cover_image_url ? (
                  <img
                    src={cardIssue.cover_image_url}
                    alt={cardIssue.title || 'Opportunity Post Cover'}
                    data-testid="hero-card-cover-img"
                  />
                ) : (
                  <div className="ic-cover-fallback" />
                )}
              </div>

              {/* Issue title */}
              {cardIssue?.title && (
                <div className="ic-issue-title" data-testid="hero-card-issue-title">
                  {cardIssue.title}
                </div>
              )}

              {/* Metadata */}
              {cardIssue?.issue_number && (
                <div className="ic-meta-row">
                  {fmtFullMeta(cardIssue.issue_date, cardIssue.volume_number, cardIssue.issue_number)}
                </div>
              )}

              {/* Stats */}
              <div className="ic-stats">Monthly Edition</div>

              {/* CTAs — single primary action */}
              <div className="ic-ctas">
                {cardIssue && (
                  <Link to={`/magazine/${cardIssue.slug}`} className="ic-read-btn" data-testid="hero-card-read-btn">
                    Read Online Now
                  </Link>
                )}
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <div className="trust-bar fade-in" data-testid="trust-bar">
        <div className="wrap trust-inner">
          <div className="trust-item">
            <span className="trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
            </span>
            <span className="trust-label">Strategic visibility &amp; partnership alignment</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
            </span>
            <span className="trust-label">Digital edition with interactive features</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            </span>
            <span className="trust-label">Learning, knowledge, professional growth</span>
          </div>
          <div className="trust-item">
            <span className="trust-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </span>
            <span className="trust-label">Over 100K combined monthly readers</span>
          </div>
        </div>
      </div>

      {/* ── DIGITAL EDITION WIDGET ── */}
      <div className="wrap" id="digital" style={{ paddingBottom: 'var(--sp6)' }}>
        <div className="dig-widget fade-in" data-testid="digital-edition-section">
          <div className="dig-inner">
            <div className="dig-copy">
              <div className="dig-kicker">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
                The Full Experience
              </div>
              <h2 className="dig-h2">Get Our <em>Interactive</em><br />Digital Edition</h2>
              <p className="dig-desc">Every article. Every video. Every advertiser link. Gamification, embedded forms, and immersive storytelling — all in one beautifully designed digital magazine. This is the complete Opportunity Post experience.</p>
              <p style={{ color: 'rgba(255,255,255,.58)', fontSize: 'var(--text-xs)', lineHeight: 1.65, maxWidth: '52ch', marginBottom: 'var(--sp6)', fontStyle: 'italic' }}>
                The online issue is a streamlined reading version of Opportunity Post Magazine. Interactive features, live sessions, member experiences, giveaways, and enhanced digital content are available through the Interactive Digital Edition.
              </p>
              <div className="dig-features">
                {['Full video integration','Interactive gamification','Embedded CTAs & forms','Clickable advertiser links','Shareable & always accessible','All articles — nothing cut'].map(f => (
                  <div className="dig-feat" key={f}><span className="dig-feat-dot" />{f}</div>
                ))}
              </div>
              <div className="dig-ctas">
                {hasRealDigital ? (
                  <a href={latestIssue.fliphtml5_embed_url} target="_blank" rel="noopener noreferrer" className="btn btn-gold" data-testid="digital-edition-cta">Explore the Interactive Digital Edition &rarr;</a>
                ) : latestIssue ? (
                  <Link to={`/magazine/${latestIssue.slug}`} className="btn btn-gold" data-testid="digital-edition-cta">Explore the Interactive Digital Edition &rarr;</Link>
                ) : (
                  <a href="#subscribe" className="btn btn-gold" data-testid="digital-edition-cta">Explore the Interactive Digital Edition &rarr;</a>
                )}
              </div>
            </div>
            <div className="dig-visual">
              <div className="dig-glow" />
              <div className="dig-book">
                <div className="dig-book-spine" />
                <div className="dig-book-badge">INTERACTIVE</div>
                <div className="dig-book-art"><div className="dig-book-art-inner" /></div>
                <div className="dig-book-foot">
                  <div className="dig-book-title">Opportunity Post</div>
                  <div className="dig-book-sub">Magazine &bull; {latestIssue?.issue_date || 'Current Issue'}</div>
                </div>
              </div>
              <div className="dig-badge-wrap">&#9654; Videos &amp; More Inside</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── LATEST ISSUE ── */}
      {latestIssue && (
        <section className="latest" id="latest" data-testid="latest-issue">
          <div className="wrap">
            <div className="sc fade-in" style={{ overflow: 'hidden' }}>
              <div className="ci-grid">

                {/* Left: editorial content */}
                <div className="ci-left">
                  <h2 className="sec-heading">Read the Current Issue</h2>
                  <div className="meta-row">
                    {latestIssue.issue_number
                      ? <span>{fmtFullMeta(latestIssue.issue_date, latestIssue.volume_number, latestIssue.issue_number)}</span>
                      : <span>{latestIssue.issue_date}</span>
                    }
                    <span>Monthly Edition</span>
                    <span>14 min read</span>
                  </div>
                  <div className="story-head" data-testid="current-issue-title">{latestIssue.title}</div>
                  <p style={{ marginTop: '1rem', color: 'var(--muted)', fontSize: 'var(--text-sm)', lineHeight: 1.75, maxWidth: '54ch' }}>
                    This month&apos;s edition explores where capital, policy, and leadership intersect to create real opportunity. Inside: education investment shifts, top policy moves, business stories driving community impact, and the people behind the momentum.
                  </p>
                  <div className="inline-btns">
                    <Link to={`/magazine/${latestIssue.slug}`} className="btn btn-dark" data-testid="current-issue-read-btn">Read the Online Issue &rarr;</Link>
                    <a href="#digital" className="btn btn-ghost">Explore the Interactive Digital Edition</a>
                  </div>
                </div>

                {/* Middle: TOC */}
                <div className="ci-middle" data-testid="current-issue-toc">
                  <h3>Inside This Issue</h3>
                  {tocItems.length > 0 ? tocItems.map((item, i) => (
                    <div className="toc-row" key={i} data-testid={`toc-item-${i}`}>
                      <div className="toc-dot" />
                      <div>
                        <strong>{typeof item === 'string' ? item : item.title}</strong>
                        {typeof item !== 'string' && item.label && <span>{item.label}</span>}
                      </div>
                    </div>
                  )) : (
                    <>
                      <div className="toc-row"><div className="toc-dot" /><div><strong>Lead Story</strong><span>Editorial</span></div></div>
                      <div className="toc-row"><div className="toc-dot" /><div><strong>Sector Signals</strong><span>Analysis</span></div></div>
                      <div className="toc-row"><div className="toc-dot" /><div><strong>Policy Watch</strong><span>Government</span></div></div>
                      <div className="toc-row"><div className="toc-dot" /><div><strong>Business Spotlight</strong><span>Economy</span></div></div>
                      <div className="toc-row"><div className="toc-dot" /><div><strong>Community Focus</strong><span>Impact</span></div></div>
                    </>
                  )}
                </div>

                {/* Right: dominant cover */}
                <div className="ci-cover" data-testid="current-issue-cover">
                  {latestIssue.cover_image_url ? (
                    <img
                      src={latestIssue.cover_image_url}
                      alt={latestIssue.title || 'Current Issue Cover'}
                    />
                  ) : (
                    <>
                      <div className="ci-cover-fallback" />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, zIndex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', color: '#FFFDF8', lineHeight: 1 }}>OP</div>
                        <p style={{ color: '#FFFDF8', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>{latestIssue.issue_date}</p>
                      </div>
                    </>
                  )}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,29,48,.25) 0%, transparent 35%)', pointerEvents: 'none' }} />
                </div>

              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── MID GRID: FEATURES + ARCHIVE SIDEBAR ── */}
      <div className="wrap mid-grid" id="inside">
        <div className="sc fade-in feat-pad">
          <h2 className="sec-heading">What You Get Every Month</h2>
          <div className="feat-grid">
            <article className="feat-card" data-testid="feature-lead-stories">
              <div className="feat-icon">
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>
              </div>
              <h3>Lead Stories</h3>
              <p>In-depth coverage of the most important developments in education, business, and policy.</p>
            </article>
            <article className="feat-card" data-testid="feature-sector-signals">
              <div className="feat-icon">
                <svg viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
              </div>
              <h3>Sector Signals</h3>
              <p>Real-time trends and market shifts curated to help leaders act with clarity and confidence.</p>
            </article>
            <article className="feat-card" data-testid="feature-interactive-video">
              <div className="feat-icon">
                <svg viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
              </div>
              <h3>Interactive Video</h3>
              <p>Embedded video features and multimedia storytelling only in the Digital Edition.</p>
            </article>
            <article className="feat-card" data-testid="feature-executive-analysis">
              <div className="feat-icon">
                <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              </div>
              <h3>Executive Analysis</h3>
              <p>Clear editorial perspective from trusted voices — under 15 minutes to complete.</p>
            </article>
          </div>
        </div>
        <aside id="archive" className="sc fade-in archive-pad" data-testid="magazine-archive">
          {(() => {
            // Previous Issue selection: most-recent OP-CDN-cover issue that is NOT the hero card issue and NOT in the hide list
            const OP_CDN = 'ik.imagekit.io';
            const PREV_HIDE = ['august-2026','jan-feb-2026','opportunity-post-monthly-jan-feb-2026-sample'];
            const withCover = issues.filter(i =>
              i.cover_image_url &&
              i.cover_image_url.includes(OP_CDN) &&
              i.slug !== cardIssue?.slug &&
              !PREV_HIDE.includes(i.slug)
            );
            const prev = withCover.sort((a, b) => (b.issue_number || 0) - (a.issue_number || 0))[0]
              || issues.find(i => i.cover_image_url && i.slug !== cardIssue?.slug && !PREV_HIDE.includes(i.slug))
              || null;
            if (!prev) return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                <div className="archive-head">
                  <h2 className="sec-heading" style={{ fontSize: '1.5rem' }}>Previous Issue</h2>
                </div>
                <p style={{ color: 'var(--muted)', fontSize: 'var(--text-xs)', marginTop: '.5rem' }}>Coming soon.</p>
              </div>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="prev-issue-card">
                {/* Section header */}
                <div className="archive-head" style={{ marginBottom: '1.1rem' }}>
                  <h2 className="sec-heading" style={{ fontSize: '1.5rem' }}>Previous Issue</h2>
                  <Link to="/magazine/archive" style={{ color: 'var(--teal)', fontSize: 'var(--text-xs)', fontWeight: 800, textDecoration: 'none' }} data-testid="view-all-link">
                    View all &rarr;
                  </Link>
                </div>

                {/* Cover image */}
                <div style={{ borderRadius: 10, overflow: 'hidden', position: 'relative', flex: '1 1 auto', minHeight: 260, maxHeight: 360 }} data-testid="prev-issue-cover-wrap">
                  {prev.cover_image_url ? (
                    <img
                      src={prev.cover_image_url}
                      alt={prev.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      data-testid="prev-issue-cover-img"
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', minHeight: 260, background: 'linear-gradient(160deg,#081D30 0%,#102F43 55%,#006E6A 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(255,253,248,.12)', border: '2px solid rgba(255,253,248,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFDF8', fontWeight: 900, fontSize: 16 }}>OP</div>
                      <p style={{ color: '#FFFDF8', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', textAlign: 'center', padding: '0 16px' }}>
                        {prev.title?.includes('2026') || !prev.title ? (prev.issue_date || 'COVER') : prev.title.toUpperCase()}<br />COVER COMING SOON
                      </p>
                    </div>
                  )}
                  {/* Subtle bottom gradient overlay */}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(8,29,48,.45) 0%,transparent 50%)', pointerEvents: 'none' }} />
                </div>

                {/* Metadata + title */}
                <div style={{ paddingTop: '0.9rem' }}>
                  <p style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--teal)', marginBottom: '0.3rem' }} data-testid="prev-issue-meta">
                    {prev.issue_number ? fmtFullMeta(prev.issue_date, prev.volume_number, prev.issue_number) : prev.issue_date}
                  </p>
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--navy)', letterSpacing: '-0.02em', lineHeight: 1.3, marginBottom: '0.75rem' }} data-testid="prev-issue-title">
                    {prev.title}
                  </h3>

                  {/* CTAs */}
                  <Link
                    to={`/magazine/${prev.slug}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--navy)', color: '#fff', padding: '0.55rem 1.1rem', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none', transition: 'background .2s' }}
                    data-testid="prev-issue-read-btn"
                  >
                    Read Online &rarr;
                  </Link>
                </div>
              </div>
            );
          })()}
        </aside>
      </div>

      {/* ── TESTIMONIALS ── */}
      <section className="testi-section" data-testid="testimonials-section">
        <div className="wrap">
          <h2 className="sec-heading fade-in" style={{ marginBottom: 0 }}>What our readers are saying</h2>
          <div className="testi-grid">
            <div className="testi-card fade-in" data-testid="testimonial-0">
              <p className="testi-quote">&ldquo;Opportunity Post is the one magazine I forward to my entire leadership team. Every issue delivers signal, not noise.&rdquo;</p>
              <div className="testi-attr"><strong>Superintendent, Urban School District</strong>Atlanta, GA</div>
            </div>
            <div className="testi-card fade-in" data-testid="testimonial-1">
              <p className="testi-quote">&ldquo;The Digital Edition is on another level. The videos and interactive content make this feel like a premium media experience.&rdquo;</p>
              <div className="testi-attr"><strong>VP of Strategy, Education Nonprofit</strong>Chicago, IL</div>
            </div>
            <div className="testi-card fade-in" data-testid="testimonial-2">
              <p className="testi-quote">&ldquo;I&apos;ve discovered more relevant partnerships and policy updates through this magazine than any other source I subscribe to.&rdquo;</p>
              <div className="testi-attr"><strong>Director of Community Engagement</strong>Washington, D.C.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BOTTOM PAIR ── */}
      <div className="wrap bottom-pair">
        <div id="advertise" className="sc fade-in advertise-card" data-testid="advertise-section">
          <div className="advertise-copy">
            <div className="kicker">For Advertisers &amp; Partners</div>
            <h2 className="sec-heading">Reach a high-intent audience</h2>
            <p>Position your brand alongside premium editorial reaching educators, policy leaders, and business decision-makers. Placements include print, Digital Edition, and custom partnerships.</p>
            <div className="stats-row">
              <div className="stat-item"><strong>100K+</strong><span>Monthly readers</span></div>
              <div className="stat-item"><strong>60%</strong><span>Director-level+</span></div>
              <div className="stat-item"><strong>U.S.</strong><span>Nationwide reach</span></div>
            </div>
            <div className="advert-btns">
              <Link to="/contact" className="btn btn-dark" data-testid="request-media-kit-btn">Request Media Kit &rarr;</Link>
              <Link to="/contact" className="btn btn-ghost" data-testid="inquire-advertising-btn">Inquire About Advertising</Link>
            </div>
          </div>
          <div className="gold-orb-visual" />
        </div>
        <div id="subscribe" className="sc fade-in signup-card" data-testid="subscribe-section">
          <div className="signup-copy">
            <h2 className="sec-heading">Sign Up Today for a Free Membership</h2>
            <p>Join thousands of leaders, educators, and innovators who receive Opportunity Post every month. Free to read online — sign up for the full Digital Edition experience.</p>
            <ul className="check-list" role="list">
              <li>Free monthly digital edition</li>
              <li>Breaking news &amp; policy alerts</li>
              <li>Exclusive subscriber content</li>
              <li>No spam, ever</li>
            </ul>
            <Link
              to="/subscribe?product=magazine"
              className="btn btn-primary"
              data-testid="magazine-subscribe-cta"
              style={{ display: 'inline-block', textDecoration: 'none' }}
            >
              Get Free Membership
            </Link>
          </div>
          <div className="mail-visual">
            <div className="mail-env" />
            <div className="mail-thumb">
              <div className="mail-thumb-art" />
              <div className="mail-thumb-label">OP</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};




// ─── TOC SECTION TYPE LABELS ──────────────────────────────────────────────────
const PAGE_TYPE_LABELS = {
  front_cover:          'Front Cover',
  inside_front_sponsor: 'Inside Front Cover',
  editors_note_page:    "Editor's Note",
  table_of_contents:    'Contents',
  stay_smart:           'Stay Smart',
  forward_brief:        'Forward',
  opportunity_signal:   'The Opportunity Signal',
  executive_in_focus:   'Executive in Focus',
  in_focus:             'In Focus',
  editorial_article:    'Editorial',
  greg_feature:         'Greg the Graduate',
  gregs_crossroads:     "Greg's Crossroads",
  elite_8:              'The Elite 8',
  opportunity_pass:     'The Opportunity Pass',
  advertiser_index:     'Advertiser Index',
  inside_back_sponsor:  'Inside Back Cover',
  back_cover:           'Back Cover',
};

const SECTION_COLORS = {
  'Front Matter':  { bg: '#F0FDF4', accent: '#059669' },
  'Departments':   { bg: '#EFF6FF', accent: '#2563EB' },
  'Cover Story':   { bg: '#FFF7ED', accent: '#D97706' },
  'Features':      { bg: '#F5F3FF', accent: '#7C3AED' },
  'Greg Universe': { bg: '#FEF2F2', accent: '#DC2626' },
  'Opportunities': { bg: '#F0FFFE', accent: '#0E9488' },
  'Perks':         { bg: '#FFFBEB', accent: '#B45309' },
  'Back Matter':   { bg: '#F9FAFB', accent: '#6B7280' },
};

// ─── Advertiser Lightbox ─────────────────────────────────────────────────────
const AdvertiserLightbox = ({ page, onClose }) => {
  if (!page) return null;
  const name    = page.sponsor_name || page.title || 'Advertiser';
  const imgUrl  = page.page_image_url;
  const pdfUrl  = (!imgUrl && page.embed_url) ? page.embed_url : null;
  const siteUrl = page.cta_url;
  const posLabel = {
    inside_front_sponsor: 'Inside Front Cover',
    inside_back_sponsor:  'Inside Back Cover',
    sponsor_full_page:    'Full Page',
  }[page.page_type] || 'Sponsor';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,8,13,0.88)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
      data-testid="advertiser-lightbox-overlay">
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: 'var(--op-paper-2)', boxShadow: '0 40px 80px rgba(0,0,0,0.7)' }}
        onClick={e => e.stopPropagation()}
        data-testid="advertiser-lightbox">

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-opacity hover:opacity-70"
          style={{ background: 'rgba(5,8,13,0.55)', color: 'white' }}
          data-testid="lightbox-close-btn">
          <X size={15} />
        </button>

        {/* Ad content */}
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={name}
            className="w-full object-contain max-h-80"
            style={{ background: '#F3F4F6', display: 'block' }}
            data-testid="lightbox-ad-image" />
        ) : (
          <div
            className="w-full flex flex-col items-center justify-center gap-4 py-12 px-8"
            style={{ background: 'linear-gradient(145deg,var(--op-navy) 0%,var(--op-teal-deep) 100%)', minHeight: 200 }}
            data-testid="lightbox-ad-placeholder">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg"
              style={{ background: 'rgba(255,253,248,.12)', border: '1.5px solid rgba(255,253,248,.25)', color: '#FFFDF8' }}>
              OP
            </div>
            <p className="font-bold text-center text-base" style={{ color: '#FFFDF8', letterSpacing: '-0.02em' }}>
              {name}
            </p>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-xl transition-opacity hover:opacity-80"
                style={{ background: 'rgba(255,253,248,.15)', color: '#FFFDF8', border: '1px solid rgba(255,253,248,.2)' }}
                data-testid="lightbox-view-pdf-btn">
                View Full Ad <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}

        {/* Info footer */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-black text-base leading-tight"
                style={{ color: 'var(--op-ink)', letterSpacing: '-0.03em' }}>
                {name}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'var(--op-muted)' }}>
                p.{page.page_number} · {posLabel}
              </p>
            </div>
            {siteUrl && (
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-opacity hover:opacity-80"
                style={{ background: 'var(--op-navy)', color: 'white' }}
                data-testid="lightbox-visit-advertiser-btn">
                Visit Advertiser <ExternalLink size={13} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Premium Editorial Article Card ──────────────────────────────────────────
const EditorialArticleCard = ({ page, contentItem }) => {
  const heroImage = contentItem?.hero_image_url || contentItem?.featured_image_url;
  const dek       = contentItem?.dek || page.blurb;
  const title     = page.title || contentItem?.title || PAGE_TYPE_LABELS[page.page_type] || 'Article';

  // ── Hero-image layout (vertical, much more prominent) ──────────────────────
  if (heroImage) {
    return (
      <div className="group rounded-2xl overflow-hidden"
        style={{ background: 'var(--op-paper-2)', border: '1px solid var(--op-border)', boxShadow: 'var(--op-shadow-sm)' }}
        data-testid="editorial-article-card"
        data-testid2="page-preview-card">

        {/* Hero image */}
        <div className="relative overflow-hidden" style={{ height: 240 }}>
          <img src={heroImage} alt={title}
            className="w-full h-full object-cover"
            onError={e => { e.target.style.display = 'none'; }}
            data-testid="editorial-hero-image" />
          {/* Page badge overlay */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="px-2.5 py-1 rounded-lg text-xs font-black"
              style={{ background: 'rgba(8,29,48,.85)', color: 'white', backdropFilter: 'blur(4px)' }}>
              p.{page.page_number}
            </span>
            {page.section_name && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-widest"
                style={{ background: 'rgba(0,110,106,.85)', color: 'white', backdropFilter: 'blur(4px)' }}
                data-testid="page-section-name">
                {page.section_name}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col gap-2.5">
          <h3 className="font-black leading-tight"
            style={{ fontSize: 'clamp(1.1rem,2.5vw,1.35rem)', color: 'var(--op-ink)', letterSpacing: '-0.04em', lineHeight: 1.15 }}>
            {title}
          </h3>
          {dek && (
            <p className="text-sm leading-relaxed" style={{ color: 'var(--op-muted)', letterSpacing: '-0.01em' }}>
              {dek}
            </p>
          )}
          {page.cta_url && (
            <div className="pt-1">
              <Link to={page.cta_url}
                className="inline-flex items-center gap-1.5 text-sm font-bold transition-all group-hover:gap-3"
                style={{ color: 'var(--op-navy)' }}
                data-testid="page-cta-link">
                {page.cta_label || 'Read Full Article'} <ChevronRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Horizontal layout (no hero image) ─────────────────────────────────────
  return (
    <div className="group rounded-2xl overflow-hidden flex flex-col sm:flex-row transition-shadow duration-300 hover:shadow-md"
      style={{ background: 'var(--op-paper-2)', border: '1px solid var(--op-border)', boxShadow: 'var(--op-shadow-sm)' }}
      data-testid="editorial-article-card"
      data-testid2="page-preview-card">

      {/* Left spine — page number + type */}
      <div className="flex-shrink-0 flex flex-col items-center justify-start px-4 py-6 sm:py-8"
        style={{ minWidth: 76, background: 'linear-gradient(160deg,var(--op-navy) 0%,var(--op-teal-deep) 100%)' }}>
        <span className="font-black text-3xl leading-none" style={{ color: '#FFFDF8' }}>
          {page.page_number}
        </span>
        <span className="mt-2 font-bold text-center uppercase"
          style={{ color: 'rgba(255,253,248,.45)', fontSize: '0.58rem', letterSpacing: '0.1em', lineHeight: 1.3 }}>
          {PAGE_TYPE_LABELS[page.page_type] || 'Article'}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 flex flex-col gap-2 min-w-0">
        {page.section_name && (
          <span className="text-xs font-bold uppercase tracking-widest"
            style={{ color: 'var(--op-teal)' }}
            data-testid="page-section-name">
            {page.section_name}
          </span>
        )}
        <h3 className="font-black leading-tight"
          style={{ fontSize: 'clamp(1rem,2.5vw,1.2rem)', color: 'var(--op-ink)', letterSpacing: '-0.035em' }}>
          {title}
        </h3>
        {dek && (
          <p className="text-sm leading-relaxed" style={{ color: 'var(--op-muted)', letterSpacing: '-0.01em' }}>
            {dek}
          </p>
        )}
        {page.cta_url && (
          <div className="mt-auto pt-3">
            <Link to={page.cta_url}
              className="inline-flex items-center gap-1.5 text-sm font-bold transition-all group-hover:gap-3"
              style={{ color: 'var(--op-navy)' }}
              data-testid="page-cta-link">
              {page.cta_label || 'Read Full Article'} <ChevronRight size={15} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Category/section helpers ─────────────────────────────────────────────────
const guessSection = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('health') || t.includes('hospital') || t.includes('medical')) return 'healthcare';
  if (t.includes('educ') || t.includes('college') || t.includes('school') || t.includes('campus')) return 'education';
  if (t.includes('veteran') || t.includes('military') || t.includes('digital transform')) return 'veterans';
  if (t.includes('govern') || t.includes('biden') || t.includes('policy') || t.includes('federal') || t.includes('civic')) return 'government';
  if (t.includes('ai') || t.includes('tech') || t.includes('digital')) return 'ai_tech';
  return 'business';
};

const getCategoryIcon = (pillar = '') => {
  const cat = pillar.toLowerCase();
  const p = { size: 16, strokeWidth: 1.75 };
  if (cat.includes('health'))   return <Stethoscope {...p} />;
  if (cat.includes('educ'))     return <GraduationCap {...p} />;
  if (cat.includes('gov') || cat.includes('civic')) return <Landmark {...p} />;
  if (cat.includes('veteran'))  return <Shield {...p} />;
  if (cat.includes('ai') || cat.includes('tech')) return <Cpu {...p} />;
  if (cat.includes('money') || cat.includes('finance') || cat.includes('trend')) return <TrendingUp {...p} />;
  if (cat.includes('communit')) return <Users {...p} />;
  return <Briefcase {...p} />;
};

const toLabel = s => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

// Split article HTML into readable segments (by <h2> or into ~3 equal chunks)
const segmentBody = (html = '') => {
  if (!html) return [''];
  // Split on every <h2 that follows something (keep intro as first segment)
  const byHeading = html.split(/(?=<h2[ >])/i).filter(s => s.trim());
  if (byHeading.length >= 2) return byHeading;
  // Fallback: split into ~3 paragraph chunks
  const paras = html.split('</p>');
  if (paras.length <= 4) return [html];
  const chunkSize = Math.ceil(paras.length / 3);
  const chunks = [];
  for (let i = 0; i < paras.length; i += chunkSize) {
    const chunk = paras.slice(i, i + chunkSize).filter(Boolean).join('</p>') + '</p>';
    if (chunk.replace(/<\/p>/g, '').trim()) chunks.push(chunk);
  }
  return chunks.length >= 2 ? chunks : [html];
};

// ─── Featured Card (used inside 2-per-row carousel) ──────────────────────────
const FeaturedCard = ({ item, isActive, onSelect }) => {
  const { page, contentItem, isPlaceholder } = item;
  const img = contentItem?.hero_image_url || contentItem?.featured_image_url || null;
  const title = page.title || contentItem?.title || '';
  const dek   = contentItem?.dek || '';
  const section = page.section_name || contentItem?.primary_pillar || '';
  const canLoad = !!contentItem;

  return (
    <div
      className="flex items-start gap-2.5 rounded-xl p-2.5 transition-colors"
      style={{
        border:     isActive ? '1.5px solid var(--op-teal)' : '1px solid var(--op-border)',
        background: isActive ? 'rgba(0,110,106,.05)' : 'var(--op-paper-2)',
        minHeight:  88,
      }}>
      {/* Thumbnail */}
      <div className="flex-shrink-0 rounded-lg overflow-hidden" style={{ width: 80, height: 64 }}>
        {img ? (
          <img src={img} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"
            style={{ background: 'linear-gradient(145deg,#081D30 0%,#006E6A 100%)' }}>
            <span className="font-black" style={{ color: 'rgba(255,253,248,.18)', fontSize: '0.6rem' }}>OP</span>
          </div>
        )}
      </div>

      {/* Text area */}
      <div className="flex-1 min-w-0 flex flex-col justify-between" style={{ minHeight: 64 }}>
        <div>
          {/* Category + page badge */}
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            {section && (
              <span className="font-bold uppercase tracking-wide"
                style={{ color: 'var(--op-teal)', fontSize: '0.58rem', letterSpacing: '0.06em' }}>
                {toLabel(section)}
              </span>
            )}
            {page.page_number && (
              <span className="font-black px-1.5 py-0.5 rounded"
                style={{ background: 'var(--op-navy)', color: '#FFFDF8', fontSize: '0.58rem' }}>
                p.{page.page_number}
              </span>
            )}
          </div>
          <p className="font-black leading-tight line-clamp-2"
            style={{ color: 'var(--op-navy)', fontSize: '0.78rem', letterSpacing: '-0.03em' }}>
            {title}
          </p>
          {dek && (
            <p className="line-clamp-1 mt-0.5" style={{ color: 'var(--op-muted)', fontSize: '0.63rem' }}>{dek}</p>
          )}
        </div>

        {/* CTA */}
        {canLoad ? (
          <button
            onClick={() => onSelect(page, contentItem)}
            className="inline-flex items-center gap-1 mt-1.5 transition-opacity hover:opacity-70 self-start"
            style={{ color: isActive ? 'var(--op-teal)' : 'var(--op-muted)', fontSize: '0.67rem', fontWeight: 700 }}
            data-testid="featured-card-cta">
            {isActive ? 'Reading' : 'Read This Article'} <ChevronRight size={9} />
          </button>
        ) : (
          <span style={{ color: 'var(--op-border)', fontSize: '0.6rem', fontStyle: 'italic', marginTop: 4 }}>Coming soon online</span>
        )}
      </div>
    </div>
  );
};

// ─── Featured Story Slider ────────────────────────────────────────────────────
const FeaturedSlider = ({ slides, selectedId, onSelect }) => {
  const [pairIdx, setPairIdx] = useState(0);
  const timerRef = useRef(null);

  // Group slides into pairs of 2
  const pairs = [];
  for (let i = 0; i < slides.length; i += 2) pairs.push(slides.slice(i, i + 2));

  const resetTimer = (len) => {
    clearInterval(timerRef.current);
    if (len > 1) timerRef.current = setInterval(() => setPairIdx(i => (i + 1) % len), 16000);
  };

  useEffect(() => {
    resetTimer(pairs.length);
    return () => clearInterval(timerRef.current);
  }, [pairs.length]);  // eslint-disable-line

  if (!pairs.length) return null;

  const safeIdx = pairIdx % pairs.length;
  const currentPair = pairs[safeIdx];
  const go = dir => { const n = (safeIdx + dir + pairs.length) % pairs.length; setPairIdx(n); resetTimer(pairs.length); };

  return (
    <div data-testid="featured-slider"
      style={{ border: '1px solid var(--op-border)', borderRadius: 14, background: 'var(--op-paper-2)', padding: '10px 12px 8px' }}>
      {/* Label row */}
      <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--op-muted)', fontSize: '0.62rem' }}>
        Featured Cover Story
      </p>

      {/* Pair row with arrows */}
      <div className="flex items-stretch gap-2">
        {pairs.length > 1 && (
          <button onClick={() => go(-1)}
            className="flex-shrink-0 self-center w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: 'var(--op-paper)', border: '1px solid var(--op-border)' }}
            data-testid="slider-prev">
            <ChevronLeft size={13} style={{ color: 'var(--op-muted)' }} />
          </button>
        )}

        {/* 2 cards */}
        <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
          {currentPair.map((item, i) => (
            <FeaturedCard key={item.page?.id || i} item={item} isActive={selectedId === item.page?.id} onSelect={onSelect} />
          ))}
          {/* Pad to 2 if odd */}
          {currentPair.length === 1 && (
            <div className="rounded-xl" style={{ border: '1px dashed var(--op-border)', background: 'var(--op-cream)', minHeight: 88 }} />
          )}
        </div>

        {pairs.length > 1 && (
          <button onClick={() => go(1)}
            className="flex-shrink-0 self-center w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: 'var(--op-paper)', border: '1px solid var(--op-border)' }}
            data-testid="slider-next">
            <ChevronRight size={13} style={{ color: 'var(--op-muted)' }} />
          </button>
        )}
      </div>

      {/* Pagination dots */}
      {pairs.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {pairs.map((_, i) => (
            <button key={i}
              onClick={() => { setPairIdx(i); resetTimer(pairs.length); }}
              className="rounded-full transition-all duration-300"
              style={{ width: i === safeIdx ? 14 : 6, height: 6, background: i === safeIdx ? 'var(--op-navy)' : 'var(--op-border)' }}
              data-testid={`slider-dot-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Issue Contents Menu ──────────────────────────────────────────────────────
const ContentsMenu = ({ pubPages, tocStrings, contentItems, selectedPageId, onSelect }) => {
  const realItems = pubPages.map(p => ({
    key:        p.id,
    pageNum:    p.page_number,
    title:      p.title || contentItems[p.id]?.title || '',
    section:    p.section_name || contentItems[p.id]?.primary_pillar || 'business',
    hasContent: !!contentItems[p.id],
    page:       p,
    contentItem: contentItems[p.id] || null,
  }));

  const placeholders = (tocStrings || []).map((str, i) => ({
    key:        `toc-${i}`,
    pageNum:    null,
    title:      str,
    section:    guessSection(str),
    hasContent: false,
    page:       null,
    contentItem: null,
  }));

  const allItems = [...realItems, ...placeholders].slice(0, 8);
  if (!allItems.length) return null;

  return (
    <section data-testid="contents-menu">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1 h-5 rounded-full" style={{ background: 'var(--op-gold)' }} />
        <h2 className="font-bold text-sm" style={{ color: 'var(--op-ink)', letterSpacing: '-0.02em' }}>
          Also in This Issue
        </h2>
      </div>
      {/* 2-column compact grid */}
      <div className="grid grid-cols-2 gap-px rounded-2xl overflow-hidden"
        style={{ border: '1px solid var(--op-border)', background: 'var(--op-border)' }}>
        {allItems.map((item, i) => {
          const isSelected = item.key === selectedPageId;
          const icon = getCategoryIcon(item.section);
          return (
            <button
              key={item.key}
              onClick={() => item.hasContent && onSelect(item.page, item.contentItem)}
              disabled={!item.hasContent}
              className="flex items-center gap-2 px-2.5 text-left transition-colors"
              style={{
                height: 40,
                background: isSelected ? 'rgba(0,110,106,.07)' : 'var(--op-paper-2)',
                cursor: item.hasContent ? 'pointer' : 'default',
              }}
              data-testid={`contents-item-${i}`}>

              {/* Icon circle */}
              <div className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                style={{
                  background: isSelected ? 'var(--op-teal)' : item.hasContent ? 'rgba(0,110,106,.1)' : 'rgba(5,8,13,.05)',
                  color:      isSelected ? '#FFFDF8'         : item.hasContent ? 'var(--op-teal)' : 'var(--op-muted)',
                }}>
                {React.cloneElement(icon, { size: 13 })}
              </div>

              {/* Title + category */}
              <div className="flex-1 min-w-0">
                <p className="font-semibold leading-none truncate"
                  style={{ fontSize: '0.7rem', color: isSelected ? 'var(--op-teal)' : item.hasContent ? 'var(--op-ink)' : 'var(--op-muted)' }}>
                  {item.title}
                </p>
                <p className="truncate mt-0.5" style={{ fontSize: '0.6rem', color: 'var(--op-muted)' }}>
                  {item.pageNum ? `p.${item.pageNum} · ` : ''}{toLabel(item.section)}
                </p>
              </div>

              {/* Arrow */}
              {item.hasContent
                ? <ChevronRight size={11} className="flex-shrink-0" style={{ color: isSelected ? 'var(--op-teal)' : 'var(--op-border)' }} />
                : null
              }
            </button>
          );
        })}
      </div>
    </section>
  );
};

// ─── Article Reader Stage ─────────────────────────────────────────────────────
const READER_PILLAR_STYLE = {
  healthcare: { background: 'var(--op-teal)',  color: '#fff' },
  government:  { background: 'var(--op-navy)', color: '#fff' },
  business:    { background: 'var(--op-gold)', color: '#fff' },
  education:   { background: '#5E3F8F',        color: '#fff' },
};

const ArticleReaderStage = ({ item, page, pubPages, contentItems, onSelect, issue, loading }) => {
  const [segIdx, setSegIdx] = useState(0);

  // Segment the article body whenever item changes
  const segments = useMemo(() => segmentBody(item?.body), [item?.id]);  // eslint-disable-line

  // Reset to first segment when article changes
  useEffect(() => { setSegIdx(0); }, [item?.id]);  // eslint-disable-line

  const linkedPages  = pubPages.filter(p => !!contentItems[p.id]);
  const currentIdx   = page ? linkedPages.findIndex(p => p.id === page.id) : -1;
  const hasPrevStory = currentIdx > 0;
  const hasNextStory = currentIdx >= 0 && currentIdx < linkedPages.length - 1;
  const heroImg      = item?.hero_image_url || item?.featured_image_url || null;
  const pillarStyle  = item ? (READER_PILLAR_STYLE[item.primary_pillar] || { background: 'var(--op-navy)', color: '#fff' }) : null;

  const totalParts = segments.length;
  const isLastPart = segIdx >= totalParts - 1;
  const isFirstPart = segIdx === 0;

  // Segment-first nav: advance segment, then story
  const handleNext = () => {
    if (!isLastPart) { setSegIdx(i => i + 1); }
    else if (hasNextStory) { const p = linkedPages[currentIdx + 1]; onSelect(p, contentItems[p.id]); }
  };
  const handlePrev = () => {
    if (!isFirstPart) { setSegIdx(i => i - 1); }
    else if (hasPrevStory) { const p = linkedPages[currentIdx - 1]; onSelect(p, contentItems[p.id]); }
  };

  const showPrev = item && (!isFirstPart || hasPrevStory);
  const showNext = item && (!isLastPart || hasNextStory);

  return (
    <section data-testid="article-reader-stage"
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-1 h-5 rounded-full" style={{ background: 'var(--op-teal)' }} />
        <h2 className="font-bold text-sm" style={{ color: 'var(--op-ink)', letterSpacing: '-0.02em' }}>
          {item ? 'Reading' : 'Reader Stage'}
        </h2>
        {item && page && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'var(--op-cream)', color: 'var(--op-muted)', border: '1px solid var(--op-border)' }}>
            p.{page.page_number}
          </span>
        )}
        {item && totalParts > 1 && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(0,110,106,.1)', color: 'var(--op-teal)' }}
            data-testid="segment-indicator">
            Part {segIdx + 1} of {totalParts}
          </span>
        )}
      </div>

      {/* Controlled-height reader card — stretches to fill sidebar height */}
      <div className="rounded-2xl overflow-hidden flex flex-col"
        style={{
          border: '1.5px solid var(--op-border)',
          background: '#FFFFFF',
          boxShadow: '0 6px 32px rgba(5,8,13,.09)',
          flex: 1,
          minHeight: 420,
        }}
        data-testid="reader-stage-card">

        {/* ── DEFAULT: no article selected ── */}
        {!item && !loading && (
          <div className="flex flex-col h-full" data-testid="reader-default-state">
            {issue?.cover_image_url ? (
              <div className="relative overflow-hidden flex-shrink-0" style={{ height: 160 }}>
                <img src={issue.cover_image_url} alt={issue?.title}
                  className="w-full h-full object-cover"
                  data-testid="reader-issue-cover" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(to top,rgba(8,29,48,.72) 0%,transparent 55%)' }} />
                <div className="absolute bottom-4 left-5">
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,253,248,.72)' }}>
                    {issue?.issue_date} · Current Issue
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-shrink-0 flex items-center justify-center"
                style={{ height: 140, background: 'linear-gradient(145deg,#081D30 0%,#006E6A 100%)' }}>
                <span className="font-black text-6xl" style={{ color: 'rgba(255,253,248,.07)' }}>OP</span>
              </div>
            )}
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-6 text-center">
              <h3 className="font-black text-lg mb-2" style={{ color: 'var(--op-ink)', letterSpacing: '-0.04em' }}>
                {issue?.title}
              </h3>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--op-muted)' }}>
                {issue?.editors_note || 'Select an article from the contents above to begin reading.'}
              </p>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest"
                style={{ color: 'var(--op-teal)' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'var(--op-teal)' }} />
                Select a story above
              </div>
            </div>
          </div>
        )}

        {/* ── LOADING ── */}
        {loading && (
          <div className="flex items-center justify-center h-full" data-testid="reader-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--op-teal)' }} />
          </div>
        )}

        {/* ── ARTICLE CONTENT ── */}
        {item && !loading && (
          <>
            {/* Hero image — magazine feature */}
            {heroImg && (
              <div className="relative overflow-hidden flex-shrink-0" style={{ height: 280 }}>
                <img src={heroImg} alt={item.title}
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 35%' }}
                  data-testid="reader-article-hero" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(to top,rgba(5,8,13,.5) 0%,transparent 55%)' }} />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  {item.primary_pillar && (
                    <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full"
                      style={pillarStyle}
                      data-testid="reader-pillar-badge">
                      {toLabel(item.primary_pillar)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Article header — fixed height area */}
            <div className="px-6 pt-4 pb-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--op-border)' }}>
              {!heroImg && item.primary_pillar && (
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider mb-2"
                  style={pillarStyle}>
                  {toLabel(item.primary_pillar)}
                </span>
              )}
              <h1 className="font-black leading-tight"
                style={{ fontSize: 'clamp(1.05rem,2.5vw,1.35rem)', color: 'var(--op-navy)', letterSpacing: '-0.04em' }}
                data-testid="reader-article-title">
                {item.title}
              </h1>
              {item.dek && (
                <p className="text-xs leading-snug mt-1.5 line-clamp-2"
                  style={{ color: 'var(--op-muted)' }}
                  data-testid="reader-article-dek">
                  {item.dek}
                </p>
              )}
              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-2 text-xs"
                style={{ color: 'var(--op-muted)' }}
                data-testid="reader-meta-bar">
                {item.byline && <span className="font-semibold" style={{ color: 'var(--op-ink)' }}>By {item.byline}</span>}
                {item.published_at && <span>{new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                {item.estimated_read_minutes && (
                  <span className="flex items-center gap-1"><Clock size={11} /> {item.estimated_read_minutes} min</span>
                )}
              </div>
            </div>

            {/* Scrollable body segment — fills remaining space */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0" data-testid="reader-article-body">
              <div
                className="prose prose-sm prose-headings:text-gray-900 prose-a:text-teal-700 prose-a:no-underline hover:prose-a:underline max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(segments[segIdx] || '') }}
              />
            </div>

            {/* Fixed controls bar */}
            <div className="flex-shrink-0 px-6 py-3 flex items-center justify-between gap-3"
              style={{ borderTop: '1px solid var(--op-border)', background: 'var(--op-paper)' }}
              data-testid="reader-nav-controls">

              {/* Segment indicator */}
              <span className="text-xs font-semibold" style={{ color: 'var(--op-muted)' }}
                data-testid="reader-page-indicator">
                {totalParts > 1
                  ? `Part ${segIdx + 1} of ${totalParts}`
                  : <a href={`/article/${item.slug}`} className="hover:opacity-70 transition-opacity"
                      style={{ color: 'var(--op-muted)' }}
                      data-testid="reader-standalone-link">
                      View full article <ExternalLink size={10} style={{ display:'inline', verticalAlign:'middle' }} />
                    </a>
                }
              </span>

              <div className="flex items-center gap-2">
                {showPrev && (
                  <button onClick={handlePrev}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:-translate-y-0.5"
                    style={{ background: 'var(--op-paper-2)', border: '1px solid var(--op-border)', color: 'var(--op-ink)' }}
                    data-testid="reader-prev-btn">
                    <ChevronLeft size={12} />
                    {isFirstPart ? 'Prev Story' : 'Previous'}
                  </button>
                )}
                {showNext && (
                  <button onClick={handleNext}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:-translate-y-0.5"
                    style={{ background: 'var(--op-navy)', color: '#FFFDF8' }}
                    data-testid="reader-next-btn">
                    {isLastPart ? 'Next Story' : 'Next Part'}
                    <ChevronRight size={12} />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// ─── PUBLIC MAGAZINE ISSUE PAGE ───────────────────────────────────────────────
export const MagazineIssuePage = () => {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [lightboxPage, setLightboxPage] = useState(null);
  const [contentItems, setContentItems] = useState({});
  // Reader state
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [advertiserExpanded, setAdvertiserExpanded] = useState(true);

  useEffect(() => { loadIssue(); }, [slug]);

  const loadIssue = async () => {
    try {
      const issueRes = await axios.get(`${API}/magazine/${slug}`);
      setIssue(issueRes.data);
      try {
        const pagesRes = await axios.get(`${API}/magazine/${issueRes.data.id}/pages`);
        const pagesData = pagesRes.data;
        setPages(pagesData);
        // Fetch content items for editorial pages that have content_item_id
        const EDITORIAL = ['editorial_article','in_focus','stay_smart','forward_brief',
          'opportunity_signal','executive_in_focus','greg_feature','gregs_crossroads',
          'elite_8','opportunity_pass','recurring_department'];
        const linked = pagesData.filter(p => EDITORIAL.includes(p.page_type) && p.content_item_id);
        const ciMap = {};
        if (linked.length > 0) {
          await Promise.all(linked.map(async p => {
            try {
              const ciRes = await axios.get(`${API}/content/${p.content_item_id}`);
              ciMap[p.id] = ciRes.data;
            } catch (_) {}
          }));
        }
        setContentItems(ciMap);
        // Pre-select article from ?article=slug URL param OR auto-select first linked article
        const articleSlug = new URLSearchParams(window.location.search).get('article');
        if (Object.keys(ciMap).length > 0) {
          if (articleSlug) {
            const matchPage = pagesData.find(p => ciMap[p.id]?.slug === articleSlug);
            if (matchPage && ciMap[matchPage.id]) {
              setSelectedPage(matchPage);
              setSelectedItem(ciMap[matchPage.id]);
            }
          } else {
            // Default: auto-select the first linked article (sorted by page number)
            const firstLinkedPage = pagesData
              .filter(p => ciMap[p.id])
              .sort((a, b) => a.page_number - b.page_number)[0];
            if (firstLinkedPage) {
              setSelectedPage(firstLinkedPage);
              setSelectedItem(ciMap[firstLinkedPage.id]);
            }
          }
        }
      } catch (_) { setPages([]); }
    } catch (error) {
      console.error('Failed to load magazine issue:', error);
      setIssue(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectArticle = (page, contentItem) => {
    setSelectedPage(page);
    setSelectedItem(contentItem);
    if (contentItem?.slug) setSearchParams({ article: contentItem.slug });
    setTimeout(() => {
      document.querySelector('[data-testid="article-reader-stage"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  if (loading) return <Spinner />;

  if (!issue) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--op-paper)' }}
        data-testid="reader-not-found">
        <div className="text-center">
          <h1 className="font-black text-3xl mb-4" style={{ color: 'var(--op-ink)', letterSpacing: '-0.04em' }}>
            Issue Not Found
          </h1>
          <p className="mb-6" style={{ color: 'var(--op-muted)' }}>
            This magazine issue doesn't exist or has been removed.
          </p>
          <Link to="/magazine" className="op-btn op-btn-dark" data-testid="back-magazine-btn">
            Back to Magazine
          </Link>
        </div>
      </div>
    );
  }

  // Group structured TOC entries by section
  const tocEntries = issue.toc_entries?.length > 0 ? issue.toc_entries : [];
  const sections = {};
  tocEntries.forEach(entry => {
    const sec = entry.section || 'Editorial';
    if (!sections[sec]) sections[sec] = [];
    sections[sec].push(entry);
  });
  const allSectionKeys = Object.keys(sections);

  const sponsorPages = pages
    .filter(p => ['sponsor_full_page','inside_front_sponsor','inside_back_sponsor'].includes(p.page_type))
    .sort((a, b) => a.page_number - b.page_number);

  const pubPages = pages.filter(p =>
    ['editorial_article','in_focus','stay_smart','forward_brief','opportunity_signal',
     'executive_in_focus','greg_feature','gregs_crossroads','elite_8','opportunity_pass',
     'recurring_department'].includes(p.page_type)
  );

  const SPONSOR_POSITION_LABEL = {
    inside_front_sponsor: 'Inside Front Cover',
    inside_back_sponsor:  'Inside Back Cover',
    sponsor_full_page:    'Full Page',
  };
  // Derive advertiser index from sponsorPages when issue.advertiser_index is empty
  const advertiserList = (issue.advertiser_index?.length > 0)
    ? issue.advertiser_index
    : sponsorPages.map(p => ({
        page_number:     p.page_number,
        advertiser_name: p.sponsor_name || p.title || 'Advertiser',
        category:        SPONSOR_POSITION_LABEL[p.page_type] || 'Sponsor',
      }));

  const hasContents = pubPages.length > 0 || tocEntries.length > 0 || issue.table_of_contents?.length > 0;

  // Reader stage computed data
  const featuredSlides = pubPages
    .filter(p => contentItems[p.id])
    .map(p => ({ page: p, contentItem: contentItems[p.id] }));
  const tocStrings = issue.table_of_contents || [];

  // Extend featured items with TOC placeholders up to 6 total for the 2-per-row carousel
  const tocFeaturedPlaceholders = tocStrings
    .slice(0, Math.max(0, 6 - featuredSlides.length))
    .map((title, i) => ({
      page: { id: `toc-feat-${i}`, page_number: null, title, section_name: guessSection(title) },
      contentItem: null,
      isPlaceholder: true,
    }));
  const featuredItems = [...featuredSlides, ...tocFeaturedPlaceholders].slice(0, 6);

  // Alphabetically sorted sponsor list for sidebar advertiser index
  const advertiserRailList = [...sponsorPages].sort((a, b) => {
    const nameA = (a.sponsor_name || a.title || '').toLowerCase();
    const nameB = (b.sponsor_name || b.title || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Digital copy URL: prefer fliphtml5_embed_url, fall back to download_pdf_url
  const digitalCopyUrl = issue.fliphtml5_embed_url || issue.download_pdf_url || null;

  return (
    <div style={{ background: 'var(--op-paper)', minHeight: '100vh' }} data-testid="magazine-reader">

      {/* ── Advertiser Lightbox ── */}
      {lightboxPage && (
        <AdvertiserLightbox page={lightboxPage} onClose={() => setLightboxPage(null)} />
      )}

      {/* ── Back nav ── */}
      <div style={{ borderBottom: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}>
        <div className="container-custom py-3">
          <Link to="/magazine"
            className="inline-flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-70"
            style={{ color: 'var(--op-teal)' }}
            data-testid="back-to-magazine">
            <ArrowLeft size={16} /> Back to Magazine
          </Link>
        </div>
      </div>

      {/* ── Hero / Masthead ── */}
      <section className="relative overflow-hidden"
        style={{ background: 'linear-gradient(130deg,#081D30 0%,#0A283C 55%,#006E6A 100%)' }}
        data-testid="issue-hero">

        {/* Decorative vertical rule */}
        <div className="absolute top-0 right-1/3 w-px h-full opacity-10"
          style={{ background: 'linear-gradient(to bottom,transparent,var(--op-teal),transparent)' }} />

        <div className="container-custom py-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-start gap-10">

            {/* Cover placeholder / image */}
            <div className="flex-shrink-0 w-full lg:w-52" data-testid="issue-cover-panel">
              {issue.cover_image_url ? (
                <img src={issue.cover_image_url} alt={issue.title}
                  className="w-full rounded-2xl"
                  style={{ boxShadow: '0 40px 80px rgba(0,0,0,.5)' }}
                  data-testid="issue-cover-img" />
              ) : (
                <div className="w-full rounded-2xl flex flex-col items-center justify-between px-7 py-10 relative overflow-hidden"
                  style={{ height: 300, background: 'linear-gradient(160deg,rgba(255,253,248,.08),rgba(255,253,248,.03))', border: '1px solid rgba(255,253,248,.12)', boxShadow: '0 40px 80px rgba(0,0,0,.4)' }}
                  data-testid="issue-cover-branded">
                  {/* Subtle grid */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    backgroundImage: 'linear-gradient(rgba(255,253,248,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,253,248,.03) 1px,transparent 1px)',
                    backgroundSize: '20px 20px',
                  }} />
                  <div className="relative z-10 flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center font-black text-xl"
                      style={{ background: 'rgba(0,140,134,.25)', border: '1.5px solid rgba(0,140,134,.4)', color: '#FFFDF8', boxShadow: '0 0 30px rgba(0,140,134,.2)' }}>
                      OP
                    </div>
                    <div className="text-center">
                      <p className="text-white text-xs font-bold tracking-widest uppercase" style={{ opacity: .8 }}>Opportunity Post</p>
                      <p className="text-white text-xs tracking-widest uppercase" style={{ opacity: .5 }}>Monthly</p>
                    </div>
                  </div>
                  <p className="font-black text-base text-center relative z-10"
                    style={{ color: '#FFFDF8', letterSpacing: '-0.03em', lineHeight: 1.25 }}>
                    {issue.issue_date}
                  </p>
                </div>
              )}
            </div>

            {/* Issue details */}
            <div className="flex-1">
              {/* Edition label */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                style={{ background: 'rgba(0,140,134,.18)', border: '1px solid rgba(0,140,134,.3)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--op-teal)' }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--op-teal-soft)' }}>
                  {issue.is_latest ? 'Current Issue' : 'Archive'} · Opportunity Post Monthly
                </span>
              </div>

              <h1 className="font-black leading-none mb-3"
                style={{ fontSize: 'clamp(1.75rem,4vw,2.75rem)', letterSpacing: '-0.045em', color: '#FFFDF8' }}
                data-testid="reader-title">
                {issue.title}
              </h1>

              {issue.subtitle && (
                <p className="text-lg mb-4" style={{ color: 'rgba(255,253,248,.68)', letterSpacing: '-0.02em', lineHeight: 1.5 }}
                  data-testid="reader-subtitle">
                  {issue.subtitle}
                </p>
              )}

              {issue.issue_theme && (
                <p className="text-sm mb-5 italic" style={{ color: 'rgba(255,253,248,.45)' }}>
                  Theme: {issue.issue_theme}
                </p>
              )}

              {/* Meta pills */}
              <div className="flex flex-wrap gap-3 mb-7">
                <MetaPill icon={<Calendar size={13} />} label={issue.issue_date} />
                <MetaPill icon={<Layers size={13} />} label={`${issue.page_count || 40} pages`} />
                {issue.issue_number && <MetaPill icon={<Tag size={13} />} label={fmtFullMeta(issue.issue_date, issue.volume_number, issue.issue_number)} />}
                {pubPages.length > 0 && (
                  <MetaPill icon={<FileText size={13} />} label={`${pubPages.length} article${pubPages.length !== 1 ? 's' : ''} online`} />
                )}
                {issue.issue_status && <StatusBadge status={issue.issue_status} />}
              </div>

              {/* CTA buttons */}
              <div className="flex flex-wrap gap-3" data-testid="reader-actions">
                {issue.flip_html_url && (
                  <a href={issue.flip_html_url} target="_blank" rel="noopener noreferrer"
                    className="op-btn op-btn-primary" data-testid="read-flipbook-btn">
                    <BookOpen size={16} /> Read Flipbook
                  </a>
                )}
                {issue.fliphtml5_embed_url && !issue.flip_html_url && (
                  <a href={issue.fliphtml5_embed_url} target="_blank" rel="noopener noreferrer"
                    className="op-btn op-btn-primary" data-testid="read-online-btn">
                    <BookOpen size={16} /> Read Online
                  </a>
                )}
                {issue.download_pdf_url && (
                  <a href={issue.download_pdf_url} target="_blank" rel="noopener noreferrer"
                    className="op-btn op-btn-light" style={{ color: 'var(--op-ink)' }}
                    data-testid="reader-download-btn">
                    <Download size={16} /> Download PDF
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Editor's Note (if available) ── */}
      {issue.editors_note && (
        <section style={{ borderBottom: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}
          data-testid="editors-note-section">
          <div className="container-custom py-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-6 rounded-full" style={{ background: 'var(--op-teal)' }} />
              <h2 className="font-bold text-lg" style={{ color: 'var(--op-ink)', letterSpacing: '-0.03em' }}>
                Editor's Note
              </h2>
            </div>
            <blockquote className="text-base leading-relaxed pl-5"
              style={{ borderLeft: '3px solid var(--op-teal)', color: 'rgba(5,8,13,.72)', fontStyle: 'italic', letterSpacing: '-0.01em' }}
              data-testid="editors-note-text">
              {issue.editors_note}
            </blockquote>
            {issue.editors_note_author && (
              <p className="mt-4 text-sm font-bold" style={{ color: 'var(--op-muted)' }}>
                — {issue.editors_note_author}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Body: 2-column layout (main + right rail) ── */}
      <div className="container-custom py-10">
        <div className="flex flex-col gap-8 lg:grid lg:gap-6"
          style={{ gridTemplateColumns: '1fr 288px', alignItems: 'stretch' }}>

          {/* ── MAIN COLUMN ── */}
          <main className="min-w-0 flex flex-col gap-5"
            style={{ minHeight: 0 }}>

            {/* 1. Featured Cover Story Slider — 2 per slide */}
            {featuredItems.length > 0 && (
              <FeaturedSlider
                slides={featuredItems}
                selectedId={selectedPage?.id}
                onSelect={handleSelectArticle}
              />
            )}

            {/* 2. Issue Contents Menu */}
            <ContentsMenu
              pubPages={pubPages}
              tocStrings={tocStrings}
              contentItems={contentItems}
              selectedPageId={selectedPage?.id}
              onSelect={handleSelectArticle}
            />

            {/* 3. Article Reader Stage */}
            <ArticleReaderStage
              item={selectedItem}
              page={selectedPage}
              pubPages={pubPages}
              contentItems={contentItems}
              onSelect={handleSelectArticle}
              issue={issue}
              loading={readerLoading}
            />

            {/* Advertiser Index lives in the right rail */}

          </main>

          {/* ── RIGHT RAIL ── */}
          <aside className="w-full flex-shrink-0 flex flex-col gap-4"
            style={{ alignSelf: 'start', width: 288 }}
            data-testid="issue-rail">

            {/* Issue metadata card */}
            <div className="rounded-2xl p-6"
              style={{ background: 'var(--op-paper-2)', border: '1px solid var(--op-border)', boxShadow: 'var(--op-shadow-sm)' }}
              data-testid="issue-meta-card">
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--op-muted)' }}>
                This Issue
              </p>
              <dl className="space-y-3.5">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--op-muted)' }}>Edition</dt>
                  <dd className="font-bold text-sm mt-0.5" style={{ color: 'var(--op-ink)' }}>{issue.issue_date}</dd>
                </div>
                {issue.issue_number && (
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--op-muted)' }}>Edition</dt>
                    <dd className="font-bold text-sm mt-0.5" style={{ color: 'var(--op-ink)' }}>{fmtFullMeta(issue.issue_date, issue.volume_number, issue.issue_number)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--op-muted)' }}>Pages</dt>
                  <dd className="font-bold text-sm mt-0.5" style={{ color: 'var(--op-ink)' }}>{issue.page_count || 40} pages</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--op-muted)' }}>Articles Online</dt>
                  <dd className="font-bold text-sm mt-0.5" style={{ color: 'var(--op-ink)' }}>
                    {pubPages.length} article{pubPages.length !== 1 ? 's' : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--op-muted)' }}>Advertisers</dt>
                  <dd className="font-bold text-sm mt-0.5" style={{ color: 'var(--op-ink)' }}>
                    {sponsorPages.length} placements
                  </dd>
                </div>
              </dl>
              <div className="mt-5 pt-4" style={{ borderTop: '1px solid var(--op-border)' }}>
                <StatusBadge status={issue.issue_status} />
              </div>
            </div>

            {/* Read / Download (when URLs available) */}
            {(issue.flip_html_url || issue.download_pdf_url) && (
              <div className="rounded-2xl p-5"
                style={{ background: 'linear-gradient(145deg,var(--op-navy) 0%,var(--op-navy-3) 100%)' }}
                data-testid="issue-actions-rail">
                <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,253,248,.4)' }}>
                  Read This Issue
                </p>
                <div className="space-y-2">
                  {issue.flip_html_url && (
                    <a href={issue.flip_html_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-bold w-full transition-opacity hover:opacity-80"
                      style={{ background: 'rgba(255,253,248,.1)', color: '#FFFDF8', border: '1px solid rgba(255,253,248,.15)' }}>
                      <BookOpen size={15} /> Read Flipbook
                    </a>
                  )}
                  {issue.download_pdf_url && (
                    <a href={issue.download_pdf_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-bold w-full transition-opacity hover:opacity-80"
                      style={{ background: 'rgba(255,253,248,.06)', color: 'rgba(255,253,248,.65)', border: '1px solid rgba(255,253,248,.1)' }}>
                      <Download size={15} /> Download PDF
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Interactive Digital Copy */}
            <div className="rounded-2xl p-5"
              style={{ background: 'linear-gradient(145deg,var(--op-navy) 0%,var(--op-teal-deep) 100%)' }}
              data-testid="digital-copy-module">
              <p className="text-xs font-bold uppercase tracking-widest mb-2"
                style={{ color: 'rgba(255,253,248,.45)' }}>
                Interactive Digital Copy
              </p>
              <p className="text-sm mb-4 leading-relaxed"
                style={{ color: 'rgba(255,253,248,.72)' }}>
                Get the full digital magazine experience when the interactive edition is available.
              </p>
              {digitalCopyUrl ? (
                <a href={digitalCopyUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold w-full transition-opacity hover:opacity-80"
                  style={{ background: 'rgba(255,253,248,.15)', color: '#FFFDF8', border: '1px solid rgba(255,253,248,.25)' }}
                  data-testid="digital-copy-btn">
                  <BookOpen size={14} /> Get Digital Copy
                </a>
              ) : (
                <button disabled
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold w-full cursor-not-allowed"
                  style={{ background: 'rgba(255,253,248,.07)', color: 'rgba(255,253,248,.35)', border: '1px solid rgba(255,253,248,.1)' }}
                  data-testid="digital-copy-btn-disabled">
                  Coming Soon
                </button>
              )}
            </div>

            {/* Advertiser Index A–Z */}
            {advertiserRailList.length > 0 && (
              <div className="rounded-2xl overflow-hidden"
                style={{ border: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}
                data-testid="advertiser-index">
                {/* Header */}
                <div className="px-4 py-3 flex items-center justify-between"
                  style={{ borderBottom: '1px solid var(--op-border)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--op-muted)', fontSize: '0.6rem' }}>
                    Advertiser Index
                  </p>
                  <span className="text-xs italic" style={{ color: 'var(--op-muted)', fontSize: '0.62rem' }}>A–Z</span>
                </div>
                {/* Scrollable list — max-height governs overflow when list is long */}
                <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                  <div className="divide-y" style={{ borderColor: 'var(--op-border)' }}>
                    {(advertiserExpanded ? advertiserRailList : advertiserRailList.slice(0, 8)).map(p => {
                      const adName = p.sponsor_name || p.title || 'Advertiser';
                      const posLabel = {
                        inside_front_sponsor: 'Inside Front Cover',
                        inside_back_sponsor:  'Inside Back Cover',
                        sponsor_full_page:    'Full Page',
                      }[p.page_type] || 'Full Page';
                      return (
                        <button key={p.id}
                          onClick={() => setLightboxPage(p)}
                          className="w-full flex items-center justify-between px-4 py-2 text-left transition-colors hover:bg-gray-50"
                          data-testid="advertiser-index-row">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold leading-tight truncate" style={{ color: 'var(--op-ink)', fontSize: '0.7rem' }}>
                              {adName}
                            </p>
                            <p style={{ color: 'var(--op-muted)', fontSize: '0.6rem' }}>{posLabel}</p>
                          </div>
                          <span className="font-black flex-shrink-0 ml-2" style={{ color: 'var(--op-muted)', fontSize: '0.65rem' }}>
                            p.{p.page_number}
                          </span>
                        </button>
                      );
                    })}
                    {/* Show more / less */}
                    {advertiserRailList.length > 8 && (
                      <button
                        onClick={() => setAdvertiserExpanded(e => !e)}
                        className="w-full py-2 text-center transition-colors hover:bg-gray-50"
                        style={{ color: 'var(--op-teal)' }}
                        data-testid="advertiser-show-more">
                        <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>
                          {advertiserExpanded ? 'Show less' : `Show all ${advertiserRailList.length} advertisers`}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Member access placeholder */}
            <div className="rounded-2xl p-5"
              style={{ background: 'var(--op-cream)', border: '1px solid var(--op-border)' }}
              data-testid="issue-subscribe-rail">
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--op-teal)' }}>
                Next Issue
              </p>
              <p className="font-bold text-sm mb-1.5" style={{ color: 'var(--op-ink)', letterSpacing: '-0.02em' }}>
                Stay informed
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--op-muted)' }}>
                Member subscriptions and issue notifications coming soon.
              </p>
            </div>

          </aside>
        </div>
      </div>
    </div>
  );
};

// ─── PUBLIC MAGAZINE READER V2 ────────────────────────────────────────────────
export const MagazineIssuePageV2 = () => {
  const { slug } = useParams();
  const [issue,        setIssue]        = useState(null);
  const [pages,        setPages]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [contentItems, setContentItems] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [slideDir,     setSlideDir]     = useState(null);   // 'next' | 'prev'
  const [navKey,       setNavKey]       = useState(0);
  const [isExiting,    setIsExiting]    = useState(false);
  const slideTimer = useRef(null);

  const EDITORIAL_TYPES = [
    'editorial_article','in_focus','stay_smart','forward_brief',
    'opportunity_signal','executive_in_focus','greg_feature','gregs_crossroads',
    'elite_8','opportunity_pass','recurring_department',
  ];

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data: iss } = await axios.get(`${API}/magazine/${slug}`);
        if (!active) return;
        setIssue(iss);
        try {
          const { data: pg } = await axios.get(`${API}/magazine/${iss.id}/pages`);
          if (!active) return;
          setPages(pg);
          const linked = pg.filter(p => EDITORIAL_TYPES.includes(p.page_type) && p.content_item_id);
          const ciMap = {};
          await Promise.all(linked.map(async p => {
            try { const { data: ci } = await axios.get(`${API}/content/${p.content_item_id}`); ciMap[p.id] = ci; } catch (_) {}
          }));
          if (!active) return;
          setContentItems(ciMap);
          const first = pg.filter(p => ciMap[p.id]).sort((a, b) => a.page_number - b.page_number)[0];
          if (first) { setSelectedPage(first); setSelectedItem(ciMap[first.id]); }
        } catch (_) { setPages([]); }
      } catch (e) { console.error(e); setIssue(null); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [slug]); // eslint-disable-line

  const linkedPages = pages.filter(p => contentItems[p.id]).sort((a, b) => a.page_number - b.page_number);
  const currentIdx  = selectedPage ? linkedPages.findIndex(p => p.id === selectedPage.id) : -1;
  const prevPage    = currentIdx > 0 ? linkedPages[currentIdx - 1] : null;
  const nextPage    = currentIdx >= 0 && currentIdx < linkedPages.length - 1 ? linkedPages[currentIdx + 1] : null;

  const pick = (p, dir = null) => {
    if (slideTimer.current) clearTimeout(slideTimer.current);
    if (dir) {
      setSlideDir(dir);
      setIsExiting(true);
      slideTimer.current = setTimeout(() => {
        setIsExiting(false);
        setSelectedPage(p);
        setSelectedItem(contentItems[p.id]);
        setNavKey(k => k + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 200);
    } else {
      setSlideDir(null);
      setIsExiting(false);
      setSelectedPage(p);
      setSelectedItem(contentItems[p.id]);
      setNavKey(k => k + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const tocDisplay = (() => {
    if (linkedPages.length > 0) return linkedPages.map(p => ({ page: p, title: p.title || contentItems[p.id]?.title || '', has: true }));
    if ((issue?.toc_entries || []).length > 0) return issue.toc_entries.map((e, i) => ({ page: null, title: e.title || e, has: false, i }));
    return (issue?.table_of_contents || []).map((t, i) => ({ page: null, title: t, has: false, i }));
  })();

  if (loading) return <Spinner />;

  if (!issue) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--mp-paper2)' }} data-testid="reader-v2-not-found">
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h1 style={{ fontFamily: "'Instrument Serif',Georgia,serif", fontSize: '2rem', color: 'var(--mp-navy)', marginBottom: '1rem' }}>Issue not found</h1>
        <Link to="/magazine" style={{ color: 'var(--mp-teal)', fontWeight: 700 }}>← Back to Magazine</Link>
      </div>
    </div>
  );

  const digitalUrl = issue.fliphtml5_embed_url && !issue.fliphtml5_embed_url.includes('example.com')
    ? issue.fliphtml5_embed_url
    : issue.download_pdf_url || null;

  return (
    <div className="rv2" data-testid="magazine-reader-v2">
      <style>{`
        .rv2{background:#faf7f1;min-height:100vh;font-family:'Inter',system-ui,sans-serif;color:#0d1f2c}
        .rv2-topbar{position:sticky;top:0;z-index:30;background:rgba(250,247,241,.92);border-bottom:1px solid rgba(9,28,40,.09);backdrop-filter:blur(12px)}
        .rv2-topbar-inner{max-width:1220px;margin:0 auto;padding:0 2rem;height:52px;display:flex;align-items:center;justify-content:space-between;gap:1rem}
        .rv2-back{display:inline-flex;align-items:center;gap:.5rem;font-size:.8125rem;font-weight:700;color:#0e6b65;text-decoration:none;transition:opacity .18s}
        .rv2-back:hover{opacity:.7}
        .rv2-issue-badge{display:inline-flex;align-items:center;gap:.5rem;padding:.3rem .85rem;border-radius:999px;background:rgba(9,28,40,.06);border:1px solid rgba(9,28,40,.1);font-size:.72rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#5c6a73}
        .rv2-body{max-width:1220px;margin:0 auto;padding:2.5rem 2rem 4rem;display:grid;grid-template-columns:268px 1fr;gap:2.5rem;align-items:start}
        .rv2-rail{position:sticky;top:68px;display:flex;flex-direction:column;gap:1.25rem}
        .rv2-cover{border-radius:14px;overflow:hidden;box-shadow:0 20px 55px rgba(9,28,40,.18);background:#081d30;aspect-ratio:3/4;position:relative}
        .rv2-cover img{width:100%;height:100%;object-fit:cover;display:block}
        .rv2-cover-art{position:absolute;inset:0;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.92) 0%,rgba(235,195,94,.45) 7%,transparent 15%),radial-gradient(circle at 14% 108%,rgba(18,90,98,.98) 0%,rgba(10,52,68,.98) 22%,rgba(7,36,50,1) 40%,rgba(7,36,50,1) 100%),radial-gradient(circle at 50% 94%,rgba(15,95,100,.85) 0%,rgba(15,95,100,.85) 24%,rgba(7,36,50,0) 28%)}
        .rv2-cover-brand{position:absolute;bottom:0;left:0;right:0;padding:1.2rem 1rem;background:linear-gradient(to top,rgba(7,28,40,.9) 0%,transparent 100%)}
        .rv2-cover-brand-title{font-family:'Instrument Serif',Georgia,serif;font-size:1.6rem;line-height:.95;color:#fff;letter-spacing:-.02em}
        .rv2-cover-brand-date{font-size:.65rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.5);margin-top:.3rem}
        .rv2-meta{background:rgba(255,255,255,.72);border:1px solid rgba(9,28,40,.09);border-radius:12px;padding:1rem 1.1rem}
        .rv2-meta-label{font-size:.6rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#8e9ba3;margin-bottom:.65rem}
        .rv2-meta-row{display:flex;justify-content:space-between;align-items:center;padding:.42rem 0;border-bottom:1px solid rgba(9,28,40,.06)}
        .rv2-meta-row:last-child{border-bottom:none;padding-bottom:0}
        .rv2-meta-key{font-size:.7rem;font-weight:700;color:#5c6a73}
        .rv2-meta-val{font-size:.7rem;font-weight:800;color:#0d1f2c}
        .rv2-toc{background:rgba(255,255,255,.72);border:1px solid rgba(9,28,40,.09);border-radius:12px;overflow:hidden}
        .rv2-toc-head{padding:.75rem 1.1rem;border-bottom:1px solid rgba(9,28,40,.06);font-size:.6rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#8e9ba3}
        .rv2-toc-item{display:flex;align-items:center;gap:.65rem;padding:.6rem 1.1rem;border-bottom:1px solid rgba(9,28,40,.05);cursor:pointer;transition:background .15s;text-decoration:none}
        .rv2-toc-item:last-child{border-bottom:none}
        .rv2-toc-item:hover{background:rgba(14,107,101,.06)}
        .rv2-toc-item.rv2-active{background:rgba(14,107,101,.08);border-left:2px solid #0e6b65}
        .rv2-toc-item.rv2-locked{cursor:default;opacity:.55}
        .rv2-toc-dot{width:.55rem;height:.55rem;border-radius:50%;background:#0e6b65;flex-shrink:0}
        .rv2-toc-dot--off{background:rgba(9,28,40,.18)}
        .rv2-toc-title{font-size:.75rem;font-weight:700;color:#0d1f2c;line-height:1.3;flex:1}
        .rv2-toc-item.rv2-active .rv2-toc-title{color:#0e6b65}
        .rv2-toc-pg{font-size:.62rem;font-weight:800;color:#8e9ba3;flex-shrink:0}
        .rv2-digital-cta{display:flex;align-items:center;justify-content:center;gap:.5rem;padding:.85rem 1rem;border-radius:10px;background:#091c28;color:#fff;font-size:.8rem;font-weight:800;text-decoration:none;transition:opacity .18s}
        .rv2-digital-cta:hover{opacity:.82;color:#fff}
        .rv2-main{min-width:0}
        .rv2-welcome{background:rgba(255,255,255,.68);border:1px solid rgba(9,28,40,.09);border-radius:16px;padding:2.5rem;margin-bottom:2rem}
        .rv2-welcome-kicker{font-size:.7rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#0e6b65;margin-bottom:.75rem}
        .rv2-welcome-title{font-family:'Instrument Serif',Georgia,serif;font-size:clamp(1.75rem,3vw,2.4rem);line-height:1.05;color:#091c28;margin-bottom:.75rem}
        .rv2-welcome-sub{font-size:.9375rem;color:#5c6a73;line-height:1.65;max-width:58ch;margin-bottom:1.5rem}
        .rv2-welcome-hint{display:inline-flex;align-items:center;gap:.5rem;font-size:.78rem;font-weight:700;color:#0e6b65}
        .rv2-welcome-hint span{width:.45rem;height:.45rem;border-radius:50%;background:#0e6b65;animation:rv2-pulse 2s infinite}
        @keyframes rv2-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.3)}}
        .rv2-article{background:#fff;border:1px solid rgba(9,28,40,.09);border-radius:16px;overflow:hidden;box-shadow:0 8px 32px rgba(9,28,40,.08)}
        .rv2-art-hero{height:320px;position:relative;overflow:hidden;background:#081d30}
        .rv2-art-hero img{width:100%;height:100%;object-fit:cover;object-position:center 30%}
        .rv2-art-hero-gradient{position:absolute;inset:0;background:linear-gradient(to top,rgba(9,28,40,.58) 0%,transparent 55%)}
        .rv2-art-hero-nophoto{position:absolute;inset:0;background:radial-gradient(circle at 72% 18%,rgba(235,195,94,.85) 0%,rgba(235,195,94,.4) 8%,transparent 18%),radial-gradient(circle at 10% 90%,rgba(14,107,101,.8) 0%,rgba(9,28,40,1) 35%,rgba(9,28,40,1) 100%)}
        .rv2-art-badge{position:absolute;bottom:1.1rem;left:1.35rem;display:inline-flex;align-items:center;gap:.4rem;padding:.32rem .8rem;border-radius:999px;background:rgba(14,107,101,.88);color:#fff;font-size:.65rem;font-weight:900;letter-spacing:.07em;text-transform:uppercase;backdrop-filter:blur(4px)}
        .rv2-art-header{padding:1.75rem 2rem 1.25rem;border-bottom:1px solid rgba(9,28,40,.07)}
        .rv2-art-section{font-size:.67rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase;color:#0e6b65;margin-bottom:.6rem}
        .rv2-art-title{font-family:'Instrument Serif',Georgia,serif;font-size:clamp(1.6rem,3.5vw,2.15rem);line-height:1.05;color:#091c28;letter-spacing:-.03em;margin-bottom:.65rem}
        .rv2-art-dek{font-size:1rem;line-height:1.6;color:#5c6a73;margin-bottom:.95rem;max-width:64ch}
        .rv2-art-meta{display:flex;flex-wrap:wrap;gap:1rem;font-size:.75rem;font-weight:600;color:#8e9ba3}
        .rv2-art-meta strong{color:#0d1f2c;font-weight:800}
        .rv2-art-body{padding:1.75rem 2rem 1.25rem;font-size:.9875rem;line-height:1.8;color:#1e2d38}
        .rv2-art-body h2{font-family:'Instrument Serif',Georgia,serif;font-size:1.45rem;line-height:1.1;color:#091c28;margin:1.75rem 0 .6rem;letter-spacing:-.02em}
        .rv2-art-body h3{font-size:1.05rem;font-weight:800;color:#091c28;margin:1.4rem 0 .5rem;letter-spacing:-.02em}
        .rv2-art-body p{margin-bottom:1.1em;max-width:72ch}
        .rv2-art-body a{color:#0e6b65;text-decoration:underline;text-decoration-color:rgba(14,107,101,.3)}
        .rv2-art-body ul,.rv2-art-body ol{margin-bottom:1.1em;padding-left:1.5rem}
        .rv2-art-body li{margin-bottom:.45em}
        .rv2-art-body blockquote{border-left:3px solid #0e6b65;margin:1.5em 0;padding:.5rem 1.2rem;color:#5c6a73;font-style:italic}
        .rv2-art-nav{padding:1rem 2rem;border-top:1px solid rgba(9,28,40,.07);display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-wrap:wrap;background:#faf7f1}
        .rv2-nav-btn{display:inline-flex;align-items:center;gap:.5rem;padding:.65rem 1.1rem;border-radius:10px;font-size:.8125rem;font-weight:800;text-decoration:none;transition:transform .18s,box-shadow .18s;border:none;cursor:pointer}
        .rv2-nav-btn:hover{transform:translateY(-1.5px)}
        .rv2-nav-prev{background:rgba(255,255,255,.85);border:1px solid rgba(9,28,40,.12)!important;color:#0d1f2c}
        .rv2-nav-next{background:#091c28;color:#fff;box-shadow:0 4px 14px rgba(9,28,40,.2)}
        .rv2-nav-center{font-size:.72rem;font-weight:700;color:#8e9ba3}
        @media(max-width:900px){
          .rv2-body{grid-template-columns:1fr}
          .rv2-rail{position:static}
          .rv2-cover{aspect-ratio:16/7;max-height:220px}
        }
        @media(max-width:600px){
          .rv2-body{padding:1.25rem 1rem 3rem}
          .rv2-topbar-inner{padding:0 1rem}
          .rv2-art-header{padding:1.25rem 1.25rem .9rem}
          .rv2-art-body{padding:1.25rem}
          .rv2-art-nav{padding:.75rem 1.25rem}
        }
        .rv2-main{overflow:hidden}
        @keyframes rv2-exit-left  {from{transform:translateX(0);opacity:1}to{transform:translateX(-60px);opacity:0}}
        @keyframes rv2-exit-right {from{transform:translateX(0);opacity:1}to{transform:translateX(60px);opacity:0}}
        @keyframes rv2-enter-right{from{transform:translateX(60px);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes rv2-enter-left {from{transform:translateX(-60px);opacity:0}to{transform:translateX(0);opacity:1}}
        .rv2-slide-exit-next{animation:rv2-exit-left  200ms cubic-bezier(.4,0,1,1) forwards}
        .rv2-slide-exit-prev{animation:rv2-exit-right 200ms cubic-bezier(.4,0,1,1) forwards}
        .rv2-slide-enter-next{animation:rv2-enter-right 260ms cubic-bezier(0,0,.2,1) both}
        .rv2-slide-enter-prev{animation:rv2-enter-left  260ms cubic-bezier(0,0,.2,1) both}
      `}</style>

      {/* ── Top nav bar ── */}
      <div className="rv2-topbar" data-testid="rv2-topbar">
        <div className="rv2-topbar-inner">
          <Link to="/magazine" className="rv2-back" data-testid="rv2-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            Back to Magazine
          </Link>
          <span className="rv2-issue-badge" data-testid="rv2-issue-badge">
            Opportunity Post &bull; {issue.issue_date || 'Current Issue'}
          </span>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="rv2-body">

        {/* ── Left rail ── */}
        <aside className="rv2-rail" data-testid="rv2-rail">

          {/* Cover */}
          <div className="rv2-cover" data-testid="rv2-cover">
            {issue.cover_image_url
              ? <img src={issue.cover_image_url} alt={issue.title} />
              : <><div className="rv2-cover-art" /><div className="rv2-cover-brand"><div className="rv2-cover-brand-title">Opportunity<br/>Post</div><div className="rv2-cover-brand-date">{issue.issue_date || 'Current Issue'}</div></div></>
            }
          </div>

          {/* Issue meta */}
          <div className="rv2-meta" data-testid="rv2-issue-meta">
            <div className="rv2-meta-label">This Issue</div>
            <div className="rv2-meta-row"><span className="rv2-meta-key">Edition</span><span className="rv2-meta-val">{issue.issue_date}</span></div>
            {issue.issue_number && <div className="rv2-meta-row"><span className="rv2-meta-key">Edition</span><span className="rv2-meta-val">{fmtFullMeta(issue.issue_date, issue.volume_number, issue.issue_number)}</span></div>}
            <div className="rv2-meta-row"><span className="rv2-meta-key">Pages</span><span className="rv2-meta-val">{issue.page_count || 40}</span></div>
            <div className="rv2-meta-row"><span className="rv2-meta-key">Articles</span><span className="rv2-meta-val">{linkedPages.length}</span></div>
          </div>

          {/* TOC */}
          {tocDisplay.length > 0 && (
            <div className="rv2-toc" data-testid="rv2-toc">
              <div className="rv2-toc-head">In This Issue</div>
              {tocDisplay.map((item, i) => (
                <div
                  key={item.page?.id || `toc-${i}`}
                  className={`rv2-toc-item${item.has && selectedPage?.id === item.page?.id ? ' rv2-active' : ''}${!item.has ? ' rv2-locked' : ''}`}
                  onClick={() => item.has && pick(item.page)}
                  role={item.has ? 'button' : undefined}
                  data-testid={`rv2-toc-item-${i}`}
                >
                  <span className={`rv2-toc-dot${item.has ? '' : ' rv2-toc-dot--off'}`} />
                  <span className="rv2-toc-title">{item.title}</span>
                  {item.page?.page_number && <span className="rv2-toc-pg">p.{item.page.page_number}</span>}
                </div>
              ))}
            </div>
          )}

          {/* Digital edition CTA */}
          {digitalUrl && (
            <a href={digitalUrl} target="_blank" rel="noopener noreferrer" className="rv2-digital-cta" data-testid="rv2-digital-cta">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              Get the Digital Edition
            </a>
          )}
        </aside>

        {/* ── Main reading area ── */}
        <main className="rv2-main" data-testid="rv2-main">

          {/* Welcome / no article selected */}
          {!selectedItem && (
            <div className="rv2-welcome" data-testid="rv2-welcome">
              <div className="rv2-welcome-kicker">Opportunity Post &bull; {issue.is_latest ? 'Current Issue' : 'Archive'}</div>
              <h1 className="rv2-welcome-title" data-testid="rv2-issue-title">{issue.title}</h1>
              {issue.subtitle && <p className="rv2-welcome-sub">{issue.subtitle}</p>}
              {issue.editors_note && <p className="rv2-welcome-sub" style={{ fontStyle: 'italic', borderLeft: '3px solid #0e6b65', paddingLeft: '1rem' }}>{issue.editors_note}</p>}
              {linkedPages.length > 0
                ? <div className="rv2-welcome-hint"><span />{linkedPages.length} articles — select one from the contents panel</div>
                : tocDisplay.length > 0
                  ? <div className="rv2-welcome-hint"><span />Browse the table of contents on the left</div>
                  : null
              }
            </div>
          )}

          {/* Article reading area */}
          {selectedItem && (
            <article
              key={navKey}
              className={`rv2-article${
                isExiting && slideDir === 'next' ? ' rv2-slide-exit-next' :
                isExiting && slideDir === 'prev' ? ' rv2-slide-exit-prev' :
                !isExiting && slideDir === 'next' ? ' rv2-slide-enter-next' :
                !isExiting && slideDir === 'prev' ? ' rv2-slide-enter-prev' : ''
              }`}
              data-testid="rv2-article"
            >

              {/* Hero image or decorative gradient */}
              <div className="rv2-art-hero" data-testid="rv2-art-hero">
                {selectedItem.hero_image_url || selectedItem.featured_image_url
                  ? <><img src={selectedItem.hero_image_url || selectedItem.featured_image_url} alt={selectedItem.title} /><div className="rv2-art-hero-gradient" /></>
                  : <div className="rv2-art-hero-nophoto" />
                }
                {selectedItem.primary_pillar && (
                  <span className="rv2-art-badge">{selectedItem.primary_pillar.replace(/_/g,' ')}</span>
                )}
              </div>

              {/* Article header */}
              <header className="rv2-art-header">
                {selectedPage?.section_name && <div className="rv2-art-section">{selectedPage.section_name}</div>}
                <h1 className="rv2-art-title" data-testid="rv2-article-title">{selectedItem.title}</h1>
                {selectedItem.dek && <p className="rv2-art-dek" data-testid="rv2-article-dek">{selectedItem.dek}</p>}
                <div className="rv2-art-meta" data-testid="rv2-article-meta">
                  {selectedItem.byline && <span><strong>By {selectedItem.byline}</strong></span>}
                  {selectedItem.published_at && <span>{new Date(selectedItem.published_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</span>}
                  {selectedItem.estimated_read_minutes && <span>{selectedItem.estimated_read_minutes} min read</span>}
                  {selectedPage?.page_number && <span>p.{selectedPage.page_number}</span>}
                </div>
              </header>

              {/* Article body */}
              <div className="rv2-art-body" data-testid="rv2-article-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedItem.body || '') }}
              />

              {/* Prev / Next nav */}
              <div className="rv2-art-nav" data-testid="rv2-article-nav">
                {prevPage
                  ? <button className="rv2-nav-btn rv2-nav-prev" onClick={() => pick(prevPage, 'prev')} data-testid="rv2-prev-btn">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                      {contentItems[prevPage.id]?.title || 'Previous'}
                    </button>
                  : <span />
                }
                <span className="rv2-nav-center">{currentIdx + 1} / {linkedPages.length}</span>
                {nextPage
                  ? <button className="rv2-nav-btn rv2-nav-next" onClick={() => pick(nextPage, 'next')} data-testid="rv2-next-btn">
                      {contentItems[nextPage.id]?.title || 'Next'}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    </button>
                  : <span />
                }
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
};

// ─── Sponsor Page Card ────────────────────────────────────────────────────────
const SPONSOR_LABEL = {
  inside_front_sponsor: 'Inside Front Cover',
  inside_back_sponsor:  'Inside Back Cover',
  sponsor_full_page:    'Full Page',
};

const LAYOUT_BADGE = {
  half_page:    { label: 'Half-Page', bg: 'var(--op-gold)', color: 'white' },
  stacked_half: { label: 'Stacked ½', bg: '#7C3AED',        color: 'white' },
};

// Append PDF viewer params to suppress toolbar chrome
const pdfSrc = (url) => url ? `${url}#toolbar=0&navpanes=0&scrollbar=0` : url;

// Inline PDF block — iframe with transparent click-through overlay
const PdfEmbed = ({ url, title, height = '100%' }) => (
  <div className="relative w-full overflow-hidden" style={{ height }}>
    <iframe
      src={pdfSrc(url)}
      title={title}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        border: 'none', display: 'block',
        pointerEvents: 'none',      // let the overlay handle clicks
        background: '#fff',
      }}
    />
    {/* Transparent overlay — makes full area clickable to open PDF in new tab */}
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="absolute inset-0"
      style={{ background: 'transparent', display: 'block' }}
      aria-label={`Open ${title} PDF`}
      data-testid="sponsor-asset-link"
    />
  </div>
);

const SponsorPageCard = ({ page }) => {
  const layout      = page.layout_template;
  const isHalf      = layout === 'half_page';
  const isStacked   = layout === 'stacked_half';
  const hasImage    = !!page.page_image_url;
  const hasPdf      = !!page.embed_url;
  const label       = SPONSOR_LABEL[page.page_type] || 'Full Page';
  const layoutBadge = LAYOUT_BADGE[layout];
  const sponsorName = page.sponsor_name || page.title;

  // ── Stacked half-page card (p.34: SOSU image top + Ithaca PDF bottom) ───────
  if (isStacked) {
    return (
      <div className="rounded-xl overflow-hidden flex flex-col"
        style={{ border: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}
        data-testid="sponsor-page-card">

        {/* Top — SOSU image */}
        <div className="relative overflow-hidden flex-shrink-0" style={{ height: 140 }}>
          {hasImage && (
            <img src={page.page_image_url} alt="Southeastern Oklahoma State University"
              className="w-full h-full object-cover" data-testid="sponsor-image" />
          )}
          <div className="absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shadow"
            style={{ background: 'var(--op-navy)', color: 'white', opacity: 0.92 }}>
            {page.page_number}
          </div>
          <div className="absolute top-2 right-2 text-xs font-bold px-1.5 py-0.5 rounded"
            style={{ background: '#7C3AED', color: 'white', fontSize: '0.6rem' }}>
            STACK
          </div>
          <div className="absolute bottom-0 left-0 right-0 px-2 py-1"
            style={{ background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(2px)' }}>
            <p className="text-white font-bold truncate" style={{ fontSize: '0.62rem' }}>SE Oklahoma State</p>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '2px dashed var(--op-border)' }} />

        {/* Bottom — Ithaca College PDF inline */}
        <div className="relative flex-shrink-0" style={{ height: 130 }}>
          <PdfEmbed url={page.embed_url} title="Ithaca College" height="100%" />
          <div className="absolute bottom-0 left-0 right-0 px-2 py-1 pointer-events-none"
            style={{ background: 'rgba(60,24,150,.6)', backdropFilter: 'blur(2px)' }}>
            <p className="text-white font-bold truncate" style={{ fontSize: '0.62rem' }}>Ithaca College</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-2.5 py-2 flex items-center justify-between"
          style={{ borderTop: '1px solid var(--op-border)' }}>
          <div>
            <p className="text-xs font-bold" style={{ color: 'var(--op-ink)', fontSize: '0.65rem' }}>
              SE Oklahoma / Ithaca
            </p>
            <p style={{ color: 'var(--op-muted)', fontSize: '0.6rem' }}>Stacked Half · p.{page.page_number}</p>
          </div>
          <a href={page.embed_url} target="_blank" rel="noopener noreferrer"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: '#7C3AED' }}>
            <ExternalLink size={12} />
          </a>
        </div>
      </div>
    );
  }

  // ── Standard full-page or half-page card ─────────────────────────────────────
  // Calculate aspect-ratio container height
  const aspectPt = isHalf ? '67%' : '133%';

  return (
    <div className="group rounded-xl overflow-hidden flex flex-col"
      style={{ border: '1px solid var(--op-border)', background: 'var(--op-paper-2)' }}
      data-testid="sponsor-page-card">

      {/* Asset area */}
      <div className="relative overflow-hidden" style={{ paddingTop: aspectPt, background: '#fff' }}>
        {hasImage ? (
          // JPG — inline image
          <>
            <img
              src={page.page_image_url}
              alt={sponsorName}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              data-testid="sponsor-image"
            />
            {/* Click opens image in new tab */}
            <a href={page.page_image_url} target="_blank" rel="noopener noreferrer"
              className="absolute inset-0" style={{ background: 'transparent' }}
              aria-label={`Open ${sponsorName} image`}
              data-testid="sponsor-asset-link" />
          </>
        ) : hasPdf ? (
          // PDF — native browser iframe embed
          <div className="absolute inset-0">
            <PdfEmbed url={page.embed_url} title={sponsorName} height="100%" />
          </div>
        ) : null}

        {/* Page number badge */}
        <div className="absolute top-2 left-2 w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs shadow pointer-events-none"
          style={{ background: 'var(--op-navy)', color: 'white', opacity: 0.92 }}>
          {page.page_number}
        </div>

        {/* Layout badge */}
        {layoutBadge && (
          <div className="absolute top-2 right-2 text-xs font-bold px-1.5 py-0.5 rounded pointer-events-none"
            style={{ background: layoutBadge.bg, color: layoutBadge.color, fontSize: '0.6rem' }}>
            {layoutBadge.label}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2.5 flex items-start justify-between gap-2 flex-1">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold truncate leading-tight" style={{ color: 'var(--op-ink)' }}>
            {sponsorName}
          </p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--op-muted)' }}>
            {isHalf ? 'Half-Page Listing' : label}
          </p>
        </div>
        {(hasImage || hasPdf) && (
          <a
            href={hasImage ? page.page_image_url : page.embed_url}
            target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--op-teal)' }}
            data-testid="sponsor-asset-link">
            <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  );
};

// ─── Shared small helper ──────────────────────────────────────────────────────
const MetaPill = ({ icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
    style={{ background: 'rgba(255,253,248,.12)', border: '1px solid rgba(255,253,248,.15)', color: 'rgba(255,253,248,.8)' }}>
    {icon}
    {label}
  </div>
);
